import { XOMICS_API_ORIGIN } from '../config/apiOrigins';

export const API_ORIGIN = XOMICS_API_ORIGIN;

let accessToken = null;
let refreshPromise = null;
const PREVIEW_ROLE_KEY = 'xomics-preview-role';
let previewRole = (() => {
  try {
    return String(window.sessionStorage.getItem(PREVIEW_ROLE_KEY) || '').trim().toUpperCase();
  } catch (_error) {
    return '';
  }
})();

export function getRolePreview() {
  return previewRole || null;
}

export function setRolePreview(roleCode) {
  const next = String(roleCode || '').trim().toUpperCase();
  if (next !== '' && !/^[A-Z][A-Z0-9_]{1,49}$/.test(next)) {
    throw new Error('Invalid role preview');
  }
  previewRole = next;
  try {
    if (next === '') window.sessionStorage.removeItem(PREVIEW_ROLE_KEY);
    else window.sessionStorage.setItem(PREVIEW_ROLE_KEY, next);
  } catch (_error) {
    // The in-memory value still works when storage is unavailable.
  }
}

export class XomicsApiError extends Error {
  constructor(message, { status = 0, code = null, body = null } = {}) {
    super(message);
    this.name = 'XomicsApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

const authFailureHandlers = new Set();

function setAccessToken(token) {
  accessToken = typeof token === 'string' && token.length > 0 ? token : null;
}

// The live-update socket authenticates with the same access token the REST
// calls use, sent in its first frame rather than the URL so it is never written
// to a proxy or access log.
export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
}

// Notified when a request could not be authenticated even after a refresh, so
// whoever owns the session state can drop it and send the user back to login.
export function onAuthFailure(handler) {
  authFailureHandlers.add(handler);
  return () => authFailureHandlers.delete(handler);
}

function notifyAuthFailure() {
  clearAccessToken();
  authFailureHandlers.forEach((handler) => {
    try {
      handler();
    } catch (error) {
      console.error('Auth failure handler threw:', error);
    }
  });
}

async function responseBody(response) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  return response.json();
}

function errorFrom(response, body) {
  return new XomicsApiError(
    body?.error || `XOmics request failed with status ${response.status}`,
    {
      status: response.status,
      code: body?.code || null,
      body,
    },
  );
}

async function refreshOnce() {
  const response = await fetch(`${API_ORIGIN}/Auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });
  const body = await responseBody(response);

  if (response.status === 409 && body?.code === 'REFRESH_RACE') {
    await new Promise((resolve) => window.setTimeout(resolve, 200));
    const retry = await fetch(`${API_ORIGIN}/Auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });
    const retryBody = await responseBody(retry);
    if (!retry.ok) throw errorFrom(retry, retryBody);
    setAccessToken(retryBody?.accessToken);
    return retryBody;
  }

  if (!response.ok) throw errorFrom(response, body);
  setAccessToken(body?.accessToken);
  return body;
}

export async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = refreshOnce().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function request(path, options = {}, retryAuthentication = true) {
  const headers = new Headers(options.headers || {});
  headers.set('Accept', 'application/json');
  if (options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (previewRole) headers.set('X-Xomics-Preview-Role', previewRole);

  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  const body = await responseBody(response);

  if (
    response.status === 401
    && retryAuthentication
    && path !== '/Auth/refresh'
    && path !== '/Auth/login'
  ) {
    // Access tokens are short lived: refresh once, retry once, then give up and
    // let the session owner send the user back to login.
    try {
      await refreshSession();
    } catch (refreshError) {
      notifyAuthFailure();
      throw refreshError;
    }

    try {
      return await request(path, options, false);
    } catch (retryError) {
      if (retryError instanceof XomicsApiError && retryError.status === 401) {
        notifyAuthFailure();
      }
      throw retryError;
    }
  }

  if (!response.ok) throw errorFrom(response, body);
  return body;
}

// Labels are files, not JSON. They still travel through the same authenticated
// backend as every other XOmics request, including the one refresh-and-retry
// attempt. Keeping this separate prevents a relative label route from opening
// against localhost, which is what caused the misleading "Route not found".
async function rawRequest(path, options = {}, retryAuthentication = true) {
  const headers = new Headers(options.headers || {});
  headers.set('Accept', '*/*');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (previewRole) headers.set('X-Xomics-Preview-Role', previewRole);

  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && retryAuthentication) {
    try {
      await refreshSession();
    } catch (refreshError) {
      notifyAuthFailure();
      throw refreshError;
    }
    const retry = await rawRequest(path, options, false);
    return retry;
  }

  if (!response.ok) {
    const body = await responseBody(response);
    if (response.status === 401) notifyAuthFailure();
    throw errorFrom(response, body);
  }
  return response;
}

function responseFilename(response, fallback) {
  const disposition = response.headers.get('content-disposition') || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) return decodeURIComponent(encoded[1].replace(/^"|"$/g, ''));
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || fallback;
}

export async function downloadShippingLabel(labelUrl) {
  const parsed = new URL(labelUrl, API_ORIGIN);
  if (parsed.origin !== new URL(API_ORIGIN).origin) {
    return { externalUrl: parsed.href, blob: null, filename: null };
  }
  const response = await rawRequest(`${parsed.pathname}${parsed.search}`);
  return {
    externalUrl: null,
    blob: await response.blob(),
    filename: responseFilename(response, 'shipping-label'),
  };
}

export async function login(email, password) {
  setRolePreview(null);
  const body = await request('/Auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }, false);
  setAccessToken(body?.accessToken);
  return body;
}

export async function loadCurrentAccount() {
  const body = await request('/Auth/me');
  setAccessToken(body?.accessToken);
  return body;
}

export async function loadPreviewRoles() {
  const body = await request('/Auth/preview-roles');
  return listField(body, 'roles');
}

export async function restoreSession() {
  return refreshSession();
}

// Public, read-only UI catalogue. It is intentionally available before login
// so the sign-in and invitation screens use the same database-backed wording
// as the authenticated workspace.
export async function loadXomicsTranslations(language) {
  const body = await request(
    `/Translations/ui?language=${encodeURIComponent(language)}`,
    {},
    false,
  );
  return {
    language: String(body?.language || 'en'),
    requestedLanguage: String(body?.requestedLanguage || language || 'en'),
    referenceLanguage: String(body?.referenceLanguage || 'en'),
    revision: String(body?.revision || ''),
    translations: field(body, 'translations'),
    fallbacks: listField(body, 'fallbacks'),
    languages: listField(body, 'languages'),
  };
}

export async function logout({ allDevices = false } = {}) {
  const body = await request('/Auth/logout', {
    method: 'POST',
    body: JSON.stringify({ logoutAll: allDevices }),
  }, false);
  clearAccessToken();
  setRolePreview(null);
  return body;
}

export function loadSessions() {
  return request('/Account/sessions');
}

/* ------------------------------------------------------------------ *
 * Project data (replaces the unauthenticated legacy service9 calls)
 * ------------------------------------------------------------------ */

// Reading a documented field off a response.
//
// A missing or wrong-typed key is a broken endpoint, not an empty result, so
// these throw rather than substituting []/0/false. Substituting would render an
// "Empty" panel over a server that is actually failing, and the screen would
// look calm while showing nothing — the one outcome worth never having.
function field(body, key) {
  if (body === null || typeof body !== 'object' || !(key in body)) {
    throw new XomicsApiError(`Server response is missing "${key}"`, {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return body[key];
}

function listField(body, key) {
  const value = field(body, key);
  if (!Array.isArray(value)) {
    throw new XomicsApiError(`Server sent "${key}" as ${typeof value}, expected a list`, {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return value;
}

function numberField(body, key) {
  const value = field(body, key);
  if (!Number.isFinite(value)) {
    throw new XomicsApiError(`Server sent "${key}" as ${typeof value}, expected a number`, {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return value;
}

function boolField(body, key) {
  const value = field(body, key);
  if (typeof value !== 'boolean') {
    throw new XomicsApiError(`Server sent "${key}" as ${typeof value}, expected true or false`, {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return value;
}

// Server-side cap, per endpoint (Projects.js `boundedInt`): patients default to
// 50 and cap at 200. The cap is what bounds the id list on the server's second
// query, so a page is asked for rather than a whole project.
const PATIENTS_PAGE_LIMIT = 50;

export async function loadParentProjects() {
  const body = await request('/Projects');
  return listField(body, 'parentProjects');
}

export async function createParentProject(name) {
  return field(await request('/Projects', { method: 'POST', body: JSON.stringify({ name }) }), 'project');
}

export function loadProjectConfiguration(projectId) {
  return request(`/Projects/${encodeURIComponent(projectId)}/configuration`);
}

export function saveProjectConfiguration(projectId, settings) {
  return request(`/Projects/${encodeURIComponent(projectId)}/configuration`, {
    method: 'PATCH', body: JSON.stringify({ settings }),
  });
}

export async function createPatient({ subProjectId, name, email, organizationReference }) {
  const body = await request('/Patients', {
    method: 'POST',
    body: JSON.stringify({ subProjectId, name, email, ...(organizationReference ? { organizationReference } : {}) }),
  });
  return field(body, 'patient');
}

// One patient's identity as it is stored, and the values their sex may be set to.
export async function loadPatient(patientId) {
  const body = await request(`/Patients/${encodeURIComponent(patientId)}`);
  return { patient: field(body, 'patient'), genders: listField(body, 'genders') };
}

// `changes` holds only the fields that changed: name, email, phone, dateOfBirth,
// gender.
export async function updatePatient(patientId, changes) {
  const body = await request(`/Patients/${encodeURIComponent(patientId)}`, {
    method: 'PATCH', body: JSON.stringify(changes),
  });
  return field(body, 'patient');
}

export async function createSubproject(parentProjectId, { projectId, description, organizationId = null }) {
  const body = await request(`/Projects/${encodeURIComponent(parentProjectId)}/subprojects`, {
    method: 'POST',
    body: JSON.stringify({ projectId, description, ...(organizationId === null ? {} : { organizationId }) }),
  });
  const subProject = field(body, 'subproject');
  return { ...subProject, project_id: subProject.projectId };
}

export async function loadSubprojects(parentProjectId) {
  const body = await request(`/Projects/${encodeURIComponent(parentProjectId)}/subprojects`);
  const subProjects = listField(body, 'subProjects');
  // The API returns `projectId`; screens across the app key subprojects off
  // `project_id`, so the alias is added once here instead of at every call site.
  return subProjects.map((subProject) => ({
    ...subProject,
    project_id: subProject.projectId,
  }));
}

function nullableCursorField(body, key) {
  const value = field(body, key);
  if (value === null) return null;
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new XomicsApiError(`Server sent an invalid "${key}" cursor`, {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return value;
}

// One page, not every page. Each patient already carries their uploads and the
// pipeline runs on each, so the screen needs no follow-up call per row.
async function patientPage(path, { limit, afterId }) {
  const params = new URLSearchParams({
    limit: String(limit),
    afterId: String(afterId),
  });
  const body = await request(`${path}?${params}`);
  return {
    items: listField(body, 'patients'),
    limit: numberField(body, 'limit'),
    afterId: numberField(body, 'afterId'),
    hasMore: boolField(body, 'hasMore'),
    nextAfterId: nullableCursorField(body, 'nextAfterId'),
  };
}

export function loadProjectPatients(parentProjectId, { limit = PATIENTS_PAGE_LIMIT, afterId = 0 } = {}) {
  return patientPage(`/Projects/${encodeURIComponent(parentProjectId)}/patients`, { limit, afterId });
}

// Answers patient tiles, not the identity rows /Patients/search returns, so a
// search on the project screen keeps drawing the same cards as the page it
// replaces. Indexed inside one parent project; there is no cursor to follow.
export async function searchProjectPatients(parentProjectId, term, {
  limit = PATIENTS_PAGE_LIMIT,
  signal,
} = {}) {
  const params = new URLSearchParams({ q: term, limit: String(limit) });
  const body = await request(
    `/Projects/${encodeURIComponent(parentProjectId)}/patients/search?${params}`,
    { signal },
  );
  return {
    items: listField(body, 'patients'),
    limit: numberField(body, 'limit'),
    hasMore: boolField(body, 'hasMore'),
  };
}

export function loadSubprojectPatients(parentProjectId, subprojectCode, {
  limit = PATIENTS_PAGE_LIMIT,
  afterId = 0,
} = {}) {
  return patientPage(
    `/Projects/${encodeURIComponent(parentProjectId)}/subprojects/${encodeURIComponent(subprojectCode)}/patients`,
    { limit, afterId },
  );
}

// The same indexed endpoint serves both Projects screens. Omitting projectId
// searches the estate; providing it keeps every index lookup inside one parent
// project. The AbortSignal lets the type-ahead cancel an obsolete request.
export async function searchPatients(term, {
  projectId = null,
  limit = 20,
  signal,
} = {}) {
  const params = new URLSearchParams({ q: term, limit: String(limit) });
  if (projectId !== null && projectId !== undefined) {
    params.set('projectId', String(projectId));
  }
  const body = await request(`/Patients/search?${params}`, { signal });
  return {
    items: listField(body, 'items'),
    hasMore: boolField(body, 'hasMore'),
  };
}

// Quick search's record lookup: the existing /Search route, which asks the
// patient, order, sample and shipment searches at once. Each source answers
// ok, forbidden (the caller lacks its permission) or error, and the server
// scopes every one of them; nothing here widens or narrows that.
export async function searchEverything(term, { limit, signal }) {
  const params = new URLSearchParams({ q: term, limit: String(limit) });
  const body = await request(`/Search?${params}`, { signal });
  return { query: field(body, 'query'), sources: field(body, 'sources') };
}

// Patient detail and the room-display presentation consume this same strict
// catalogue/availability/fetch contract. The browser never receives the
// Clinic Portal service credential; its normal XOmics session authorizes it.
export async function loadPatientDataTypes() {
  const body = await request('/Patient/data-types');
  return listField(body, 'dataTypes');
}

export async function loadPatientDataAvailability(patientId, dataTypeCodes) {
  const body = await request(`/Patient/${encodeURIComponent(patientId)}/data-availability`, {
    method: 'POST',
    body: JSON.stringify({ dataTypeCodes }),
  });
  return {
    patientId: numberField(body, 'patientId'),
    dataTypes: listField(body, 'dataTypes'),
    presentationDefaults: field(body, 'presentationDefaults'),
  };
}

// `includeHidden` asks for the rows hidden from reports, the app and the digital
// twin as well, each saying whether it is hidden: only for someone who may hide them.
export async function fetchPatientData(patientId, selections, { includeHidden = false } = {}) {
  const body = await request(`/Patient/${encodeURIComponent(patientId)}/data-fetch`, {
    method: 'POST',
    body: JSON.stringify(includeHidden ? { selections, includeHidden: true } : { selections }),
  });
  return listField(body, 'results');
}

// One row of a patient's result, such as a variant, hidden from everything made or
// shown after it, or shown again.
export async function setPatientResultRowHidden(patientId, jobOutputId, sourceRow, hidden) {
  const path = `/Patients/${encodeURIComponent(patientId)}/result-rows/${encodeURIComponent(jobOutputId)}`
    + `/${encodeURIComponent(sourceRow)}/hidden`;
  return request(path, { method: hidden ? 'POST' : 'DELETE' });
}

// Everything done that concerns the patient, newest first, a page at a time.
export async function loadPatientHistory(patientId, { beforeId = null } = {}) {
  const query = beforeId === null ? '' : `?before_id=${encodeURIComponent(beforeId)}`;
  const body = await request(`/Patients/${encodeURIComponent(patientId)}/history${query}`);
  return { events: listField(body, 'events'), nextBeforeId: field(body, 'nextBeforeId') };
}

export async function loadPatientExampleDataBatches(patientId) {
  const body = await request(`/Patient/${encodeURIComponent(patientId)}/example-data`);
  return listField(body, 'batches');
}

export async function injectPatientExampleData(patientId, profile) {
  const body = await request(`/Patient/${encodeURIComponent(patientId)}/example-data`, {
    method: 'POST',
    body: JSON.stringify({ profile }),
  });
  return field(body, 'batch');
}

export async function rollbackPatientExampleData(patientId, batchUuid) {
  const body = await request(
    `/Patient/${encodeURIComponent(patientId)}/example-data/${encodeURIComponent(batchUuid)}`,
    { method: 'DELETE' },
  );
  return field(body, 'batch');
}

// Report requests accumulate for as long as the portal runs, so this list pages
// like uploads rather than pulling every row the way patients do.
const REPORT_REQUESTS_PAGE_LIMIT = 50;

export async function loadReportOptions() {
  const body = await request('/Reports/options');
  return {
    reportTypes: listField(body, 'reportTypes'),
    companies: listField(body, 'companies'),
    languages: listField(body, 'languages'),
  };
}

// Returns the same shape as lookupReportPatient for each hit, so a match can fill
// the form without a second request.
export async function searchReportPatients(term, { limit = 20 } = {}) {
  const query = String(term ?? '').trim();
  // Numeric input is an exact primary-key lookup, including one- and two-digit
  // ids that are intentionally below the free-text search minimum.
  if (/^\d+$/.test(query)) {
    try {
      const patient = await lookupReportPatient(query);
      return { patients: [patient], truncated: false };
    } catch (error) {
      if (error?.status === 404) return { patients: [], truncated: false };
      throw error;
    }
  }
  const body = await request(
    `/Reports/patients?q=${encodeURIComponent(query)}&limit=${limit}`,
  );
  return {
    patients: listField(body, 'patients'),
    truncated: boolField(body, 'truncated'),
  };
}

export async function lookupReportPatient(patientId) {
  const body = await request(`/Reports/patients/${encodeURIComponent(patientId)}`);
  return field(body, 'patient');
}

export async function loadReportJobs({ contentTypeId, patientId = null }) {
  const params = new URLSearchParams({ contentTypeId: String(contentTypeId) });
  if (patientId !== null && patientId !== undefined) {
    params.set('patientId', String(patientId));
  }
  const body = await request(`/Reports/jobs?${params}`);
  return {
    reportType: field(body, 'reportType'),
    requiresPatient: boolField(body, 'requiresPatient'),
    jobs: listField(body, 'jobs'),
  };
}

export async function submitReportRequest(payload) {
  const body = await request('/Reports/requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return field(body, 'reportRequest');
}

export async function loadReportRequests({
  limit = REPORT_REQUESTS_PAGE_LIMIT,
  offset = 0,
  status = 'all',
} = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    status,
  });
  const body = await request(`/Reports/requests?${params}`);
  const statusCounts = field(body, 'statusCounts');
  if (statusCounts === null || typeof statusCounts !== 'object' || Array.isArray(statusCounts)) {
    throw new XomicsApiError('Server sent "statusCounts" in the wrong shape', {
      code: 'MALFORMED_RESPONSE',
      body,
    });
  }
  return {
    reportRequests: listField(body, 'reportRequests'),
    total: numberField(body, 'total'),
    statusCounts,
  };
}

export async function loadReportRequest(reportRequestId) {
  const body = await request(`/Reports/requests/${encodeURIComponent(reportRequestId)}`);
  return field(body, 'reportRequest');
}

export async function updateReportRequest(reportRequestId, payload) {
  const body = await request(`/Reports/requests/${encodeURIComponent(reportRequestId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return field(body, 'reportRequest');
}

export async function rerunReportRequest(reportRequestId, payload = {}) {
  const body = await request(
    `/Reports/requests/${encodeURIComponent(reportRequestId)}/rerun`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return field(body, 'reportRequest');
}

export async function loadReportDownload(reportRequestId) {
  const body = await request(
    `/Reports/requests/${encodeURIComponent(reportRequestId)}/download`,
  );
  return {
    url: field(body, 'url'),
    expiresAt: numberField(body, 'expiresAt'),
    bytes: numberField(body, 'bytes'),
    sha256: field(body, 'sha256'),
  };
}

export { REPORT_REQUESTS_PAGE_LIMIT };

// The Reporting section's report library and analytics. Read-only: a file is
// still opened through loadReportDownload, the report's own download route.
function reportLibraryParams(values) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined && value !== '' && value !== 'all') params.set(key, String(value));
  }
  return params;
}

export async function loadReportInventory(filters = {}) {
  const body = await request(`/ReportInventory/reports?${reportLibraryParams(filters)}`);
  return {
    reports: listField(body, 'reports'),
    total: numberField(body, 'total'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
  };
}

export async function loadReportFacets() {
  const body = await request('/ReportInventory/facets');
  return {
    total: numberField(body, 'total'),
    statuses: listField(body, 'statuses'),
    sources: listField(body, 'sources'),
    reasons: listField(body, 'reasons'),
    reportTypes: listField(body, 'reportTypes'),
    languages: listField(body, 'languages'),
    pipelines: listField(body, 'pipelines'),
    products: listField(body, 'products'),
    projects: listField(body, 'projects'),
    organizations: listField(body, 'organizations'),
    companies: listField(body, 'companies'),
  };
}

export async function loadReportAnalytics({ days = 30 } = {}) {
  const body = await request(`/ReportInventory/analytics?${reportLibraryParams({ days })}`);
  return {
    days: numberField(body, 'days'), since: numberField(body, 'since'), until: numberField(body, 'until'),
    step: numberField(body, 'step'), totals: field(body, 'totals'), series: listField(body, 'series'),
    sources: listField(body, 'sources'), reportTypes: listField(body, 'reportTypes'), languages: listField(body, 'languages'),
    pipelines: listField(body, 'pipelines'), products: listField(body, 'products'), projects: listField(body, 'projects'),
    organizations: listField(body, 'organizations'), companies: listField(body, 'companies'),
    unassigned: field(body, 'unassigned'), duration: field(body, 'duration'), failures: field(body, 'failures'),
    validation: field(body, 'validation'),
  };
}

// Research > Cohorts: counts for every visible project, one project or one of its
// subprojects, narrowed to the participants who have every chosen omics layer.
export async function loadCohortOverview({ project = '', subproject = '', layers = '' } = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ project, subproject, layers })) if (value) params.set(key, value);
  const body = await request(`/Cohorts/overview?${params}`);
  return {
    asOf: numberField(body, 'asOf'), scope: field(body, 'scope'), layers: listField(body, 'layers'),
    projects: listField(body, 'projects'), total: numberField(body, 'total'), participants: numberField(body, 'participants'),
    withOmics: numberField(body, 'withOmics'), multiOmics: numberField(body, 'multiOmics'), sex: field(body, 'sex'),
    age: field(body, 'age'), omics: listField(body, 'omics'), overlap: listField(body, 'overlap'), groups: listField(body, 'groups'),
  };
}

// One folder of the report library: where it sits, its subfolders with their
// counts, and how many reports lie directly in it (listed by loadReportInventory
// with the same project and patient).
export async function loadReportFolders({ project = null, patient = null } = {}) {
  const body = await request(`/ReportInventory/folders?${reportLibraryParams({ project, patient })}`);
  return { path: listField(body, 'path'), folders: listField(body, 'folders'), files: field(body, 'files') };
}

// Thousands of keys per content type, so always paged.
const TRANSLATIONS_PAGE_LIMIT = 50;

export async function loadTranslationCatalogue() {
  const body = await request('/Translations/content-types');
  return {
    contentTypes: listField(body, 'contentTypes'),
    languages: listField(body, 'languages'),
    referenceLanguage: field(body, 'referenceLanguage'),
  };
}

export async function loadTranslations({
  contentTypeId,
  languageCode,
  q = '',
  limit = TRANSLATIONS_PAGE_LIMIT,
  offset = 0,
}) {
  const params = new URLSearchParams({
    contentTypeId: String(contentTypeId),
    languageCode,
    limit: String(limit),
    offset: String(offset),
  });
  if (q) params.set('q', q);

  const body = await request(`/Translations?${params.toString()}`);
  return {
    translations: listField(body, 'translations'),
    total: numberField(body, 'total'),
    referenceLanguage: field(body, 'referenceLanguage'),
  };
}

function factOf(metadata, key) {
  return (metadata?.facts || []).find((entry) => entry.key === key)?.value ?? null;
}

// Every translation edit, newest first, across all keys -- so the panel reads as
// a version history for the content set rather than for one string.
export async function loadTranslationEdits({ limit = 100, beforeId = null } = {}) {
  const params = new URLSearchParams({
    action: 'TRANSLATION_UPDATE',
    limit: String(limit),
  });
  if (beforeId !== null) params.set('before_id', String(beforeId));

  const body = await request(`/Audit?${params.toString()}`);
  const edits = listField(body, 'events')
    .filter((event) => event.outcome === 'ok')
    .map((event) => ({
      id: event.id,
      translationId: Number(event.entity_uuid),
      contentType: factOf(event.metadata, 'content_type'),
      // Absent on rows written before the id was recorded; the caller falls back
      // to matching the display name.
      contentTypeId: factOf(event.metadata, 'content_type_id'),
      language: factOf(event.metadata, 'language'),
      translationKey: factOf(event.metadata, 'translation_key'),
      who: event.account_email || event.actor_type,
      at: Number(event.created_at),
      before: event.metadata?.changed?.value?.before ?? null,
      after: event.metadata?.changed?.value?.after ?? null,
    }));

  return { edits, nextBeforeId: body?.next_before_id ?? null };
}

// Value only; the server identifies the row.
export async function saveTranslation(id, value) {
  const body = await request(`/Translations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
  return {
    translation: field(body, 'translation'),
    unchanged: boolField(body, 'unchanged'),
  };
}

// One page of the spreadsheet export: key, English reference, current value.
export async function loadTranslationExportPage({ contentTypeId, languageCode, afterKey = '' }) {
  const params = new URLSearchParams({
    contentTypeId: String(contentTypeId),
    languageCode,
  });
  if (afterKey !== '') params.set('afterKey', afterKey);

  const body = await request(`/Translations/bulk/export?${params.toString()}`);
  return {
    rows: listField(body, 'rows'),
    hasMore: boolField(body, 'hasMore'),
    nextAfterKey: body?.nextAfterKey ?? null,
  };
}

// Pages until the server says there is no more, so the caller gets whole sheet.
// The next page is requested while the current one is handed over.
export async function loadAllTranslationsForExport({ contentTypeId, languageCode, onProgress }) {
  const rows = [];
  let afterKey = '';
  for (;;) {
    const page = await loadTranslationExportPage({ contentTypeId, languageCode, afterKey });
    rows.push(...page.rows);
    if (typeof onProgress === 'function') onProgress(rows.length);
    if (!page.hasMore || page.nextAfterKey === null) return rows;
    // The cursor is the last key read, so it must move. Bailing out beats
    // paging forever if it ever does not.
    if (page.nextAfterKey <= afterKey) {
      throw new XomicsApiError('The export stopped advancing', { code: 'EXPORT_STALLED' });
    }
    afterKey = page.nextAfterKey;
  }
}

export async function previewTranslationImport({ contentTypeId, languageCode, rows }) {
  const body = await request('/Translations/bulk/preview', {
    method: 'POST',
    body: JSON.stringify({ contentTypeId: Number(contentTypeId), languageCode, rows }),
  });
  return { changes: listField(body, 'changes'), counts: field(body, 'counts') };
}

export async function confirmTranslationImport({ contentTypeId, languageCode, changes, source }) {
  const body = await request('/Translations/bulk/confirm', {
    method: 'POST',
    body: JSON.stringify({ contentTypeId: Number(contentTypeId), languageCode, changes, source }),
  });
  return {
    applied: Number(body?.applied ?? 0),
    created: Number(body?.created ?? 0),
    updated: Number(body?.updated ?? 0),
    event: body?.event ?? null,
  };
}

// Scope and kind are optional. Set only what was given: URLSearchParams turns
// undefined into the literal string "undefined", which the server then reads as
// a nine character language code and rejects.
export async function loadTranslationEvents({
  contentTypeId = null,
  languageCode = null,
  kind = null,
  beforeId = null,
} = {}) {
  const params = new URLSearchParams();
  if (contentTypeId !== null && contentTypeId !== '') {
    params.set('contentTypeId', String(contentTypeId));
  }
  if (languageCode !== null && languageCode !== '') params.set('languageCode', languageCode);
  if (kind !== null && kind !== '') params.set('kind', kind);
  if (beforeId !== null) params.set('beforeId', String(beforeId));

  const query = params.toString();
  const body = await request(`/Translations/events${query === '' ? '' : `?${query}`}`);
  return {
    events: listField(body, 'events'),
    hasMore: boolField(body, 'hasMore'),
    nextBeforeId: body?.nextBeforeId ?? null,
  };
}

export async function loadTranslationEvent(uuid, { afterId = null } = {}) {
  const params = new URLSearchParams();
  if (afterId !== null) params.set('afterId', String(afterId));
  const query = params.toString();

  const body = await request(
    `/Translations/events/${encodeURIComponent(uuid)}${query === '' ? '' : `?${query}`}`,
  );
  return {
    event: field(body, 'event'),
    changes: listField(body, 'changes'),
    hasMore: boolField(body, 'hasMore'),
    nextAfterId: body?.nextAfterId ?? null,
  };
}

export async function rollbackTranslationEvent(uuid) {
  const body = await request(`/Translations/events/${encodeURIComponent(uuid)}/rollback`, {
    method: 'POST',
  });
  return { event: body?.event ?? null, revertedUuid: body?.revertedUuid ?? null };
}



export { TRANSLATIONS_PAGE_LIMIT };

// -------------------------------------------------------------- pipelines

export async function loadPipelineCatalogue({ scope = 'active' } = {}) {
  const body = await request(`/Pipelines?scope=${encodeURIComponent(scope)}`);
  return {
    activeRelease: field(body, 'activeRelease'),
    pipelines: listField(body, 'pipelines'),
  };
}

export async function loadPipeline(id) {
  const body = await request(`/Pipelines/${encodeURIComponent(id)}`);
  return field(body, 'pipeline');
}

export async function loadPipelineReleases({ limit = 100 } = {}) {
  const body = await request(`/Pipelines/releases?limit=${encodeURIComponent(limit)}`);
  return {
    releases: listField(body, 'releases'),
    attempts: listField(body, 'attempts'),
  };
}

export async function loadPipelineRelease(id) {
  const body = await request(`/Pipelines/releases/${encodeURIComponent(id)}`);
  return field(body, 'release');
}

export async function loadPipelineDeployment(id) {
  const body = await request(`/Pipelines/deployments/${encodeURIComponent(id)}`);
  return field(body, 'deployment');
}

export function revokeSession(sessionUuid) {
  return request(`/Account/sessions/${encodeURIComponent(sessionUuid)}/revoke`, {
    method: 'POST',
  });
}

export function updateProfile(profile) {
  return request('/Account/profile', {
    method: 'PATCH',
    body: JSON.stringify(profile),
  });
}

export async function changePassword(currentPassword, newPassword) {
  const body = await request('/Account/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  clearAccessToken();
  return body;
}

// ---------------------------------------------------------------- xomics uploads

// Everything a form needs to be built correctly: which sample types exist, which
// pipelines each permits, and what files each pipeline wants. The browser never
// hard-codes that position 1 is the R1 -- it reads it from here, because that
// knowledge belongs in one place.
export async function loadUploadCatalogue() {
  const body = await request('/Uploads/catalogue');
  return {
    // Which release these analyses came from. The server sends null when no
    // release is active, and that is an answer -- nothing can be ordered --
    // rather than a value to substitute for.
    release: field(body, 'release'),
    sampleTypes: listField(body, 'sample_types'),
    pipelines: listField(body, 'pipelines'),
  };
}

export async function loadXomicsUploads({ limit = 50, offset = 0 } = {}) {
  const body = await request(`/Uploads?limit=${limit}&offset=${offset}`);
  return {
    uploads: listField(body, 'uploads'),
    total: numberField(body, 'total'),
  };
}

// Customers' samples whose patient reports are asked for and whose data is not
// uploaded yet, at the caller's laboratory.
export async function loadRequestedUploads() {
  return listField(await request('/Uploads/requested'), 'requested');
}

export async function loadXomicsJobs({
  status = 'all', query = '', limit = 50, offset = 0,
} = {}) {
  const params = new URLSearchParams({
    status,
    limit: String(limit),
    offset: String(offset),
  });
  const needle = String(query ?? '').trim();
  if (needle !== '') params.set('q', needle);
  const body = await request(`/Uploads/jobs?${params}`);
  const counts = field(body, 'counts');
  return {
    jobs: listField(body, 'jobs'),
    counts: {
      all: numberField(counts, 'all'),
      queued: numberField(counts, 'queued'),
      running: numberField(counts, 'running'),
      completed: numberField(counts, 'completed'),
      failed: numberField(counts, 'failed'),
    },
    total: numberField(body, 'total'),
  };
}

// What the visit field should default to, and which analyses this patient has
// already had. Both are hints for the form -- neither is enforced.
export function loadNextVisit(patientId, sampleCode) {
  return request(`/Uploads/next-visit?patientId=${encodeURIComponent(patientId)}`
    + `&sample_code=${encodeURIComponent(sampleCode)}`);
}

export function loadXomicsUpload(uuid) {
  return request(`/Uploads/${encodeURIComponent(uuid)}`);
}

// Returns one signed PUT per file. The bytes never pass through the API.
export function createXomicsUpload(payload) {
  return request('/Uploads', { method: 'POST', body: JSON.stringify(payload) });
}

export function completeXomicsUpload(uuid) {
  return request(`/Uploads/${encodeURIComponent(uuid)}/complete`, { method: 'POST' });
}

// Tells the API which ETag came back for each part, so it can assemble them into a
// single object. Until this succeeds the key does not exist.
export function assembleXomicsFile(uuid, position, parts) {
  return request(
    `/Uploads/${encodeURIComponent(uuid)}/files/${encodeURIComponent(position)}/assemble`,
    { method: 'POST', body: JSON.stringify({ parts }) },
  );
}

// Straight to object storage, deliberately not through request(): this carries no
// session credentials -- the signed URL is the entire authority -- and it needs
// upload progress, which fetch cannot report.
//
// Resolves with the part's ETag. R2 returns it per part and assembly is impossible
// without it, so a missing one is an error rather than something to shrug at: it
// means the bucket is not exposing the header to this origin.
function putPart(url, blob, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new XomicsApiError(`Upload failed (${xhr.status})`, { status: xhr.status }));
        return;
      }
      const etag = (xhr.getResponseHeader('ETag') || '').replace(/"/g, '');
      if (!etag) {
        reject(new XomicsApiError(
          'Object storage did not return an ETag. The bucket CORS policy needs ExposeHeaders: ["etag"].',
        ));
        return;
      }
      resolve(etag);
    });
    xhr.addEventListener('error', () => reject(new XomicsApiError('Upload failed: network error')));
    xhr.addEventListener('abort', () => reject(new XomicsApiError('Upload cancelled')));
    xhr.send(blob);
  });
}

// How many parts are in flight at once. Above this the browser queues them anyway
// and the only effect is a progress bar that lies about what has started.
const PART_CONCURRENCY = 4;
const PART_ATTEMPTS = 3;

// Slices the file per the plan the API returned and uploads the parts, retrying an
// individual part rather than the whole file -- the point of doing this at all is
// that a drop at 90% costs one part, not six gigabytes.
export async function putSignedFile(plan, file, onProgress) {
  const total = file.size;
  const loaded = new Array(plan.parts.length).fill(0);
  const report = () => {
    if (onProgress) onProgress(loaded.reduce((sum, n) => sum + n, 0) / total);
  };

  const queue = plan.parts.map((part, index) => ({ part, index }));
  let failure = null;

  async function worker() {
    for (;;) {
      const next = queue.shift();
      if (!next || failure) return;
      const { part, index } = next;
      const blob = file.slice(part.offset, part.offset + part.bytes);

      for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt += 1) {
        try {
          const etag = await putPart(part.put_url, blob, (bytes) => {
            loaded[index] = bytes;
            report();
          });
          part.etag = etag;
          loaded[index] = part.bytes;
          report();
          break;
        } catch (error) {
          loaded[index] = 0;
          report();
          if (attempt === PART_ATTEMPTS) { failure = error; return; }
          await new Promise((r) => setTimeout(r, 500 * attempt));
        }
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(PART_CONCURRENCY, plan.parts.length) }, worker),
  );
  if (failure) throw failure;

  return plan.parts.map((part) => ({ part_number: part.part_number, etag: part.etag }));
}

// ---------------------------------------------------------------- cluster

export async function loadClusterNodes() {
  const body = await request('/Cluster');
  return listField(body, 'nodes');
}

export async function loadClusterFleet() {
  const body = await request('/Cluster');
  return {
    nodes: listField(body, 'nodes'),
    queue: body && typeof body === 'object' && body.queue ? body.queue : {
      jobs: 0,
      oldest_queued_at: null,
      requested_threads: 0,
      requested_ram_gb: 0,
      requested_storage_gb: 0,
    },
  };
}

export function loadClusterNode(id) {
  return request(`/Cluster/${encodeURIComponent(id)}`);
}

export function updateClusterNode(id, changes) {
  return request(`/Cluster/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

// Neither of these deletes anything: a node keeps its row because jobs reference
// node_id, and a token keeps its row so who issued what stays auditable.
export function decommissionClusterNode(id) {
  return request(`/Cluster/${encodeURIComponent(id)}/decommission`, { method: 'POST' });
}

// The token carries the role, so the operator never types it and a machine cannot
// choose what it becomes. Shown once; only its hash is stored.
export function mintJoinToken({ role, ttlSeconds = 900 }) {
  return request('/Cluster/join-tokens', {
    method: 'POST',
    body: JSON.stringify({ role, ttl_seconds: ttlSeconds }),
  });
}

export async function loadJoinTokens() {
  const body = await request('/Cluster/join-tokens');
  return listField(body, 'tokens');
}

export function revokeJoinToken(id) {
  return request(`/Cluster/join-tokens/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
}

export async function loadReleases() {
  const body = await request('/Cluster/releases/list');
  return listField(body, 'releases');
}

// ------------------------------------------------------------- notifications

export async function loadNotifications({ limit = 30, unreadOnly = false } = {}) {
  const query = `?limit=${limit}${unreadOnly ? '&unread=1' : ''}`;
  const body = await request(`/Notifications${query}`);
  return {
    notifications: listField(body, 'notifications'),
    unread: numberField(body, 'unread'),
  };
}

export function markNotificationRead(id) {
  return request(`/Notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

export function markAllNotificationsRead() {
  return request('/Notifications/read-all', { method: 'POST' });
}

export async function loadNotificationPreferences() {
  const body = await request('/Notifications/preferences');
  return {
    // A channel the server cannot send on, or that this account has no address
    // for, is reported so the UI can say why instead of offering a switch that
    // silently drops every message.
    emailAvailable: boolField(body, 'emailAvailable'),
    smsAvailable: boolField(body, 'smsAvailable'),
    hasEmail: boolField(body, 'hasEmail'),
    hasPhoneNumber: boolField(body, 'hasPhoneNumber'),
    topics: listField(body, 'topics'),
  };
}

export function updateNotificationPreference(topicKey, { inapp, email, sms }) {
  return request(`/Notifications/preferences/${encodeURIComponent(topicKey)}`, {
    method: 'PATCH',
    body: JSON.stringify({ inapp, email, sms }),
  });
}

// ---------------------------------------------------------------------- staff

export async function loadStaff() {
  const body = await request('/Staff');
  return {
    // False when the caller lacks STAFF_MANAGE_ELEVATED, so the screen can say
    // the list is partial rather than letting someone assume it is everyone.
    seesEveryone: boolField(body, 'seesEveryone'),
    staff: listField(body, 'staff'),
  };
}

export async function loadAssignableRoles() {
  const body = await request('/Staff/assignable-roles');
  return listField(body, 'roles');
}

export function inviteStaff(details) {
  return request('/Staff/invite', { method: 'POST', body: JSON.stringify(details) });
}

export function resendStaffInvitation(uuid) {
  return request(`/Staff/${encodeURIComponent(uuid)}/resend-invitation`, { method: 'POST' });
}

export function updateStaff(uuid, changes) {
  return request(`/Staff/${encodeURIComponent(uuid)}`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

export function deleteStaff(uuid) {
  return request(`/Staff/${encodeURIComponent(uuid)}`, { method: 'DELETE' });
}

// ------------------------------------------------------------- collections

export async function loadCollectionCatalogue() {
  const body = await request('/Collections/catalogue');
  return { products: listField(body, 'products'), templates: listField(body, 'templates'), sampleTypes: listField(body, 'sampleTypes'),
    pipelines: listField(body, 'pipelines') };
}

export async function loadCollections({ scope = 'local', state = 'all', q = '', offset = 0, limit = 50 } = {}) {
  const params = new URLSearchParams({ scope, state, q, offset: String(offset), limit: String(limit) });
  const body = await request(`/Collections?${params.toString()}`);
  return { collections: listField(body, 'collections'), total: numberField(body, 'total'), summary: field(body, 'summary'), scopeCounts: field(body, 'scopeCounts') };
}

export async function generateCollections(input) {
  const body = await request('/Collections/generate', { method: 'POST', body: JSON.stringify(input) });
  return { requestId: field(body, 'requestId'), count: numberField(body, 'count'), sampleCount: numberField(body, 'sampleCount'), collections: listField(body, 'collections') };
}

export async function loadCollection(uuid) {
  const body = await request(`/Collections/${encodeURIComponent(uuid)}`);
  return field(body, 'collection');
}

// Activations by channel, kit and place over a range, and every collection's lifecycle.
export async function loadCollectionActivity({ scope = 'local', days = 30 } = {}) {
  const params = new URLSearchParams({ scope, days: String(days) });
  const body = await request(`/Collections/activity?${params.toString()}`);
  return {
    scope: field(body, 'scope'), days: numberField(body, 'days'), since: numberField(body, 'since'), until: numberField(body, 'until'),
    step: numberField(body, 'step'), totals: field(body, 'totals'), series: listField(body, 'series'), kits: listField(body, 'kits'),
    countries: listField(body, 'countries'), cities: listField(body, 'cities'), lifecycle: field(body, 'lifecycle'),
  };
}

// The latest activations, newest first; `before` is the previous answer's `next`.
export async function loadCollectionActivations({ scope = 'local', limit = 8, before = null } = {}) {
  const params = new URLSearchParams({ scope, limit: String(limit) });
  if (before !== null) { params.set('beforeAt', String(before.beforeAt)); params.set('beforeId', String(before.beforeId)); }
  const body = await request(`/Collections/activations?${params.toString()}`);
  return { activations: listField(body, 'activations'), next: field(body, 'next') };
}

export async function loadCollectionPrinters() {
  const body = await request('/Devices?purpose=specimen_labels');
  return listField(body, 'devices');
}

// The printers and scanners of the signed-in account's location.
export async function loadDevices() {
  const body = await request('/Devices');
  return { devices: listField(body, 'devices'), canManage: field(body, 'canManage') === true, wifiNetwork: field(body, 'wifiNetwork') || null };
}

export async function loadDeviceTypes() {
  const body = await request('/Devices/types');
  return listField(body, 'types');
}

export async function addDevice(typeCode) {
  const body = await request('/Devices', { method: 'POST', body: JSON.stringify({ typeCode }) });
  return field(body, 'device');
}

export async function removeDevice(deviceId) {
  return request(`/Devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' });
}

// A fresh key for the printer and everything the setup wizard hands it.
export async function startPrinterPairing(deviceId) {
  return request(`/Devices/${encodeURIComponent(deviceId)}/pairing`, { method: 'POST' });
}

export async function saveLocationWifi(networkName, key) {
  return request('/Devices/wifi', { method: 'POST', body: JSON.stringify({ networkName, key }) });
}

export async function forgetLocationWifi() {
  return request('/Devices/wifi', { method: 'DELETE' });
}

export async function setPrinterLabelSize(deviceId, widthMm, heightMm) {
  return request(`/Devices/${encodeURIComponent(deviceId)}/label`, { method: 'POST', body: JSON.stringify({ widthMm, heightMm }) });
}

export async function printTestLabel(deviceId) {
  return request(`/Devices/${encodeURIComponent(deviceId)}/test-label`, { method: 'POST' });
}

export async function loadShippingPrinters() {
  const body = await request('/Devices?purpose=shipping_labels');
  return listField(body, 'devices');
}

// Prints a carrier label on a shipping-label printer of this location.
export async function printShippingLabel(labelUrl, deviceId) {
  const parsed = new URL(labelUrl, API_ORIGIN);
  return request(`${parsed.pathname}/print`, { method: 'POST', body: JSON.stringify({ deviceId }) });
}

export async function printCollectionLabels(uuid, deviceId) {
  const body = await request(`/Collections/${encodeURIComponent(uuid)}/print`, {
    method: 'POST', body: JSON.stringify({ deviceId }),
  });
  return { uuid: field(body, 'uuid'), printedAt: numberField(body, 'printedAt'), deviceId: field(body, 'deviceId') };
}

export async function loadProductCollectionOptions(productId) {
  const body = await request(`/Products/${encodeURIComponent(productId)}/collection-options`);
  return { templateVersionId: field(body, 'templateVersionId'), pipelineId: numberField(body, 'pipelineId'), templates: listField(body, 'templates'),
    sampleTypes: listField(body, 'sampleTypes') };
}

export function setProductCollectionDefault(productId, templateVersionId) {
  return request(`/Products/${encodeURIComponent(productId)}/collection-default`, { method: 'PATCH', body: JSON.stringify({ templateVersionId }) });
}

// -------------------------------------------------------------------- kits

export async function loadKits({
  limit = 50, offset = 0, state = '', productId = '', locationId = '', scope = 'local',
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  params.set('scope', scope);
  if (state !== '') params.set('state', state);
  if (productId !== '') params.set('productId', String(productId));
  if (locationId !== '') params.set('locationId', String(locationId));
  const body = await request(`/Kits?${params.toString()}`);
  return {
    kits: listField(body, 'kits'),
    total: numberField(body, 'total'),
    summary: field(body, 'summary'),
  };
}

// A collection by its own code or by the code of one of its tubes; `tubeCode`
// names the tube when a tube's code was given.
export async function findCollectionByCode(code) {
  const body = await request(`/Samples/by-code/${encodeURIComponent(code)}`);
  return { ...field(body, 'collection'), tubeCode: field(body, 'tubeCode'), printedTubeCode: body?.printedTubeCode ?? null };
}

export async function findKitByCode(code, { scope = 'local' } = {}) {
  const body = await request(
    `/Kits/by-code/${encodeURIComponent(code)}?scope=${encodeURIComponent(scope)}`,
  );
  return field(body, 'kit');
}

export async function loadKit(uuid, { scope = 'local' } = {}) {
  const body = await request(
    `/Kits/${encodeURIComponent(uuid)}?scope=${encodeURIComponent(scope)}`,
  );
  return field(body, 'kit');
}

export async function searchKits({
  prefix, limit = 20, state = '', locationId = '', scope = 'local',
}) {
  const params = new URLSearchParams({
    prefix: String(prefix), limit: String(limit), scope,
  });
  if (state !== '') params.set('state', state);
  if (locationId !== '') params.set('locationId', String(locationId));
  const body = await request(`/Kits/search?${params.toString()}`);
  return {
    prefix: field(body, 'prefix'),
    kits: listField(body, 'kits'),
    hasMore: boolField(body, 'hasMore'),
  };
}

// The location is the caller's own, taken from their account server-side.
export function generateKits(productId, count) {
  return request('/Kits/generate', {
    method: 'POST', body: JSON.stringify({ productId, count }),
  });
}

export function markKitsPrinted(uuids) {
  return request('/Kits/mark-printed', { method: 'POST', body: JSON.stringify({ uuids }) });
}

export async function setKitPrintState(uuid, state) {
  const body = await request(`/Kits/${encodeURIComponent(uuid)}/print-state`, {
    method: 'PATCH', body: JSON.stringify({ state }),
  });
  return field(body, 'kit');
}

// ----------------------------------------------------------------- samples

// The journey itself. The UI reads it rather than knowing it, so a step added
// to the table appears without a release.
export async function loadSampleStages() {
  const body = await request('/Samples/stages');
  return listField(body, 'stages');
}

export async function loadSamples({
  limit = 50, offset = 0, stageCode = '', actor = '', locationId = '', productId = '',
  open = false, scope = 'local',
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  params.set('scope', scope);
  if (stageCode !== '') params.set('stageCode', stageCode);
  if (actor !== '') params.set('actor', actor);
  if (locationId !== '') params.set('locationId', String(locationId));
  if (productId !== '') params.set('productId', String(productId));
  if (open) params.set('open', '1');
  const body = await request(`/Samples?${params.toString()}`);
  return {
    scope: field(body, 'scope'),
    samples: listField(body, 'samples'),
    total: numberField(body, 'total'),
    counts: field(body, 'counts'),
  };
}

export async function searchSamples({
  query, limit = 20, stageCode = '', scope = 'local',
} = {}) {
  const params = new URLSearchParams({ query: String(query), limit: String(limit), scope });
  if (stageCode !== '') params.set('stageCode', stageCode);
  const body = await request(`/Samples/search?${params.toString()}`);
  return {
    query: field(body, 'query'),
    scope: field(body, 'scope'),
    samples: listField(body, 'samples'),
    hasMore: boolField(body, 'hasMore'),
  };
}

export async function loadSampleActivity({
  limit = 50, stageCode = '', scope = 'local', query = '',
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), scope });
  if (stageCode !== '') params.set('stageCode', stageCode);
  if (query.trim() !== '') params.set('query', query.trim());
  const body = await request(`/Samples/activity?${params.toString()}`);
  return {
    scope: field(body, 'scope'),
    query: field(body, 'query'),
    events: listField(body, 'events'),
    limit: numberField(body, 'limit'),
    hasMore: boolField(body, 'has_more'),
  };
}

export async function loadSample(uuid) {
  const body = await request(`/Samples/${encodeURIComponent(uuid)}`);
  return {
    sample: field(body, 'sample'),
    history: listField(body, 'history'),
    labWorkflows: listField(body, 'labWorkflows'),
    // A customer's sample ({ reportsAsked }), or null.
    customerSample: field(body, 'customerSample'),
    organization: field(body, 'organization'),
    tubes: listField(body, 'tubes'),
    mayRecordOrganizationCodes: field(body, 'mayRecordOrganizationCodes'),
    // Null unless the suggested step is a transit one, which is the only time
    // there is anywhere to choose between.
    destinations: field(body, 'destinations'),
  };
}

export function activateKit({ code, patientId, note }) {
  return request('/Samples/activate', {
    method: 'POST', body: JSON.stringify({ code, patientId, note }),
  });
}

export function recordSampleStep(uuid, step) {
  return request(`/Samples/${encodeURIComponent(uuid)}/events`, {
    method: 'POST', body: JSON.stringify(step),
  });
}

export async function loadLocations(kind = '') {
  const query = kind === '' ? '' : `?kind=${encodeURIComponent(kind)}`;
  const body = await request(`/Directory/locations${query}`);
  return listField(body, 'locations');
}

export async function loadManagedLocations() {
  const body = await request('/Locations');
  return {
    locations: listField(body, 'locations'),
    responsibilities: listField(body, 'responsibilities'),
  };
}

export async function searchLocationPlaces(query, regionCode = null) {
  const body = await request('/Locations/places/search', {
    method: 'POST',
    body: JSON.stringify({ query, regionCode }),
  });
  return listField(body, 'suggestions');
}

export async function loadLocationPlace(placeId) {
  const body = await request('/Locations/places/details', {
    method: 'POST',
    body: JSON.stringify({ placeId }),
  });
  return field(body, 'location');
}

export function createLocation(details) {
  return request('/Locations', { method: 'POST', body: JSON.stringify(details) });
}

export function updateLocation(id, details) {
  return request(`/Locations/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

export function updateLocationCountries(id, countryCodes) {
  return request(`/Locations/${encodeURIComponent(id)}/countries`, {
    method: 'PATCH', body: JSON.stringify({ countryCodes }),
  });
}

// Makes an office the website default: the office whose store a country gets
// that no office is responsible for. It takes the mark from the one that had it.
export function setLocationWebsiteDefault(id) {
  return request(`/Locations/${encodeURIComponent(id)}/website-default`, { method: 'PATCH', body: '{}' });
}

// Scoped to a location, because the carrier must collect from that place.
export async function loadCarriers(locationId) {
  const query = locationId === undefined ? '' : `?locationId=${encodeURIComponent(locationId)}`;
  const body = await request(`/Directory/carriers${query}`);
  return listField(body, 'carriers');
}

// ------------------------------------------------------------------ orders

const ORDERS_PAGE_LIMIT = 50;

export async function loadOrders({
  limit = ORDERS_PAGE_LIMIT, offset = 0, email = '', scope = 'local', status = 'all',
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  params.set('scope', scope);
  params.set('status', status);
  if (email !== '') params.set('email', email);
  const body = await request(`/Orders?${params.toString()}`);
  return { orders: listField(body, 'orders'), total: numberField(body, 'total') };
}

export async function loadOrder(uuid, { scope = 'local' } = {}) {
  const body = await request(`/Orders/${encodeURIComponent(uuid)}?scope=${encodeURIComponent(scope)}`);
  return {
    order: field(body, 'order'),
    items: listField(body, 'items'),
    payments: listField(body, 'payments'),
    shipments: listField(body, 'shipments'),
  };
}

export async function loadOrderBoard({ from, to, scope = 'local' }) {
  const params = new URLSearchParams({ from: String(from), to: String(to), scope });
  const body = await request(`/Orders/board?${params.toString()}`);
  return {
    range: field(body, 'range'),
    placed: listField(body, 'placed'),
    dispatched: listField(body, 'dispatched'),
    unsent: listField(body, 'unsent'),
    unsentTotal: numberField(body, 'unsentTotal'),
  };
}

export async function searchOrders({ query, status = 'all', limit = 20, scope = 'local' }) {
  const params = new URLSearchParams({
    query: String(query), status, limit: String(limit), scope,
  });
  const body = await request(`/Orders/search?${params.toString()}`);
  return {
    query: field(body, 'query'),
    status: field(body, 'status'),
    scope: field(body, 'scope'),
    orders: listField(body, 'orders'),
    hasMore: boolField(body, 'hasMore'),
  };
}

// --------------------------------------------------------------- shipments

const SHIPMENTS_PAGE_LIMIT = 100;

export async function loadShipments({
  scope = 'active', subject = 'all', status = '', query = '',
  limit = SHIPMENTS_PAGE_LIMIT, offset = 0, locationScope = 'local',
} = {}) {
  const params = new URLSearchParams({
    scope,
    subject,
    limit: String(limit),
    offset: String(offset),
    locationScope,
  });
  if (status !== '') params.set('status', status);
  if (query.trim() !== '') params.set('query', query.trim());
  const body = await request(`/Shipments?${params.toString()}`);
  return {
    scope: field(body, 'scope'),
    locationScope: field(body, 'locationScope'),
    subject: field(body, 'subject'),
    status: field(body, 'status'),
    query: field(body, 'query'),
    shipments: listField(body, 'shipments'),
    total: numberField(body, 'total'),
    hasMore: boolField(body, 'hasMore'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
    summary: field(body, 'summary'),
  };
}

export async function loadShipment(uuid, { locationScope = 'local' } = {}) {
  const body = await request(
    `/Shipments/${encodeURIComponent(uuid)}?locationScope=${encodeURIComponent(locationScope)}`,
  );
  return {
    shipment: field(body, 'shipment'),
    items: listField(body, 'items'),
  };
}

// ------------------------------------------------------------- validation

export async function loadValidations({
  scope = 'local', status = 'pending', limit = 50, offset = 0,
} = {}) {
  const params = new URLSearchParams({
    scope, status, limit: String(limit), offset: String(offset),
  });
  const body = await request(`/Validation?${params.toString()}`);
  return {
    scope: field(body, 'scope'),
    status: field(body, 'status'),
    validations: listField(body, 'validations'),
    total: numberField(body, 'total'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
  };
}

// The validation queue a person at a time: each person with the project they
// come from and their results in the chosen status. `total` counts people.
export async function loadValidationPeople({
  scope = 'local', status = 'pending', limit = 25, offset = 0,
} = {}) {
  const params = new URLSearchParams({
    scope, status, limit: String(limit), offset: String(offset),
  });
  const body = await request(`/Validation/people?${params.toString()}`);
  return {
    scope: field(body, 'scope'),
    status: field(body, 'status'),
    people: listField(body, 'people'),
    statusCounts: field(body, 'statusCounts'),
    total: numberField(body, 'total'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
  };
}

// Whether the signed-in validator can send a booking follow-up: their email
// must be a clinic portal account, the clinician the patient books with.
export function loadBookingEligibility() {
  return request('/Validation/booking-eligibility');
}

// emails: { sendResults, sendBooking } for an approval; left out, the server's
// defaults apply (a kit's report email, a general collection's results email).
export function decideValidation(collectionUuid, status, revision, note = null, emails = {}) {
  return request(`/Validation/${encodeURIComponent(collectionUuid)}`, {
    method: 'PATCH', body: JSON.stringify({ status, revision, note, ...emails }),
  });
}

// -------------------------------------------------------------- laboratory

export async function loadLaboratorySamples({ limit = 100, offset = 0, query = '', status = 'all', sampleType = '' } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset), query, status, sampleType });
  const body = await request(`/Laboratory/samples?${params.toString()}`);
  return {
    laboratory: field(body, 'laboratory'),
    samples: listField(body, 'samples'),
    total: numberField(body, 'total'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
    filters: field(body, 'filters'),
    statusCounts: field(body, 'statusCounts'),
    sampleTypes: listField(body, 'sampleTypes'),
  };
}

export async function loadLaboratoryCollection(uuid) {
  const body = await request(`/Laboratory/collections/${encodeURIComponent(uuid)}`);
  return {
    laboratory: field(body, 'laboratory'), collection: field(body, 'collection'),
    pipelines: listField(body, 'pipelines'), unmappedPipelines: listField(body, 'unmappedPipelines'),
    // What set the workflow its tubes were sent for (kit, clinic, customer or request), or null when the lab chooses.
    set: field(body, 'set'),
    analyses: listField(body, 'analyses'), tubes: listField(body, 'tubes'), workOrders: listField(body, 'workOrders'),
    canAssign: field(body, 'canAssign'), mayAssign: field(body, 'mayAssign'),
  };
}

export function assignLaboratoryWorkflows(collectionUuid, assignments) {
  return request('/Laboratory/assign-workflows', {
    method: 'POST', body: JSON.stringify({ collectionUuid, assignments }),
  });
}

export async function loadLaboratoryWork(options = {}) {
  const body = await request(`/Laboratory/work?${laboratoryQuery(options)}`);
  return {
    laboratory: field(body, 'laboratory'), collections: listField(body, 'collections'),
    total: numberField(body, 'total'), limit: numberField(body, 'limit'), offset: numberField(body, 'offset'),
    filters: field(body, 'filters'), statusCounts: field(body, 'statusCounts'),
  };
}

export async function loadLaboratoryApprovals() {
  const body = await request('/Laboratory/approvals');
  return { laboratory: field(body, 'laboratory'), stageRecords: listField(body, 'stageRecords'), acceptances: listField(body, 'acceptances') };
}

export function recordLaboratoryStage(body) {
  return request('/Laboratory/stage-records', { method: 'POST', body: JSON.stringify(body) });
}

export function reviewLaboratoryStage(recordUuid, body) {
  return request(`/Laboratory/stage-records/${encodeURIComponent(recordUuid)}/review`, { method: 'POST', body: JSON.stringify(body) });
}

export async function loadLaboratoryScanners() {
  const body = await request('/Laboratory/scanners');
  return { laboratory: field(body, 'laboratory'), accountId: numberField(body, 'accountId'), scanners: listField(body, 'scanners') };
}

export function resolveLaboratoryCode(code) {
  return request(`/Laboratory/resolve-code?${new URLSearchParams({ code }).toString()}`);
}

export function receiveLaboratorySample(body) {
  return request('/Laboratory/receive-scan', { method: 'POST', body: JSON.stringify(body) });
}

// `department` names the side of the laboratory, for a tube whose sample type both sides take.
export function loadLaboratoryAcceptance(sampleUuid, department = '') {
  return request(`/Laboratory/samples/${encodeURIComponent(sampleUuid)}/acceptance${department === '' ? '' : `?department=${encodeURIComponent(department)}`}`);
}

export function recordLaboratoryAcceptance(body) {
  return request('/Laboratory/acceptances', { method: 'POST', body: JSON.stringify(body) });
}

export function reviewLaboratoryAcceptance(acceptanceUuid, body) {
  return request(`/Laboratory/acceptances/${encodeURIComponent(acceptanceUuid)}/review`, { method: 'POST', body: JSON.stringify(body) });
}

export async function loadLaboratoryWorkflows() {
  const body = await request('/Laboratory/workflows');
  return { workflows: listField(body, 'workflows'), nextActions: listField(body, 'nextActions') };
}

export async function loadLaboratoryWorkflowVersion(id) {
  const body = await request(`/Laboratory/workflow-versions/${encodeURIComponent(id)}`);
  return { workflow: field(body, 'workflow'), nextActions: listField(body, 'nextActions'), units: listField(body, 'units') };
}

export async function loadLaboratoryAnalyses() {
  return listField(await request('/Laboratory/analyses'), 'analyses');
}

export async function loadLaboratoryAcceptanceRules() {
  return listField(await request('/Laboratory/acceptance-rules'), 'ruleSets');
}

export async function loadLaboratoryDevices() {
  const body = await request('/Laboratory/devices');
  return { laboratory: field(body, 'laboratory'), devices: listField(body, 'devices'), mayManage: field(body, 'mayManage') };
}

export function changeLaboratoryDeviceStatus(id, status, note) {
  return request(`/Laboratory/devices/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status, note }) });
}

// Prints a machine's own label (its inventory number to scan) on a label printer of the laboratory.
export function printLaboratoryDeviceLabel(id, deviceId) {
  return request(`/Laboratory/devices/${encodeURIComponent(id)}/label`, { method: 'POST', body: JSON.stringify({ deviceId }) });
}

// ------------------------------------------ customers' submissions at the laboratory

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = name;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// A file's link, opened in the tab the click opened (opened first, so the browser does not block it).
async function openLink(load) {
  const opened = window.open('', '_blank');
  try {
    const { url } = await load();
    if (opened) opened.location.href = url;
    else window.location.assign(url);
  } catch (error) {
    if (opened) opened.close();
    throw error;
  }
}

const customerBoxPath = (uuid, number) => `/Laboratory/customer-submissions/${encodeURIComponent(uuid)}/forms/${encodeURIComponent(number)}`;

// The boxes customers sent to this laboratory, newest first, with where their samples are.
export function loadLaboratoryCustomerSubmissions(options = {}) {
  return request(`/Laboratory/customer-submissions?${laboratoryQuery(options)}`);
}

// One box: its samples (their names beside our codes, our QC results, the customer's answers) and the files of both sides.
export function loadLaboratoryCustomerSubmission(uuid, number) {
  return request(customerBoxPath(uuid, number));
}

// Our labels for a box's tubes, on the laboratory's specimen-label printer: our code to scan, with the
// customer's name for each sample under it.
export function printLaboratoryCustomerLabels(uuid, number, deviceId) {
  return request(`${customerBoxPath(uuid, number)}/labels`, { method: 'POST', body: JSON.stringify({ deviceId }) });
}

// The box as the laboratory's Sample Receiving and QC Verification Form, saved where the browser saves downloads.
export async function downloadLaboratoryReceivingForm(uuid, number) {
  const response = await rawRequest(`${customerBoxPath(uuid, number)}/receiving-form`);
  saveBlob(await response.blob(), responseFilename(response, 'receiving-form.xlsx'));
}

// Our QC reports for the customer, each sent as { name, base64 }; and one taken away again.
export function addLaboratoryCustomerFiles(uuid, number, files) {
  return request(`${customerBoxPath(uuid, number)}/files`, { method: 'POST', body: JSON.stringify({ files }) });
}
export function removeLaboratoryCustomerFile(uuid, number, fileUuid) {
  return request(`${customerBoxPath(uuid, number)}/files/${encodeURIComponent(fileUuid)}`, { method: 'DELETE' });
}

// The customer told their QC results are in, with what we say with them.
export function sendLaboratoryQcResults(uuid, number, message) {
  return request(`${customerBoxPath(uuid, number)}/qc-results`, { method: 'POST', body: JSON.stringify({ message: message || null }) });
}

// A customer's answer that came another way, recorded for them: { answers: [{ position, answer }], note }.
export function answerLaboratoryCustomerQc(uuid, number, body) {
  return request(`${customerBoxPath(uuid, number)}/qc-answers`, { method: 'POST', body: JSON.stringify(body) });
}

// A file of a customer's submission, in a new tab.
export function openLaboratoryCustomerFile(uuid) {
  return openLink(() => request(`/Laboratory/customer-files/${encodeURIComponent(uuid)}`));
}

// What the laboratory shares with a customer: nothing to do with an upload for analysis. A project's
// path, how much is shared on it, the files of the whole project or of one sample (by its place), which
// sample each file name belongs to, and one time of sharing with its note.
const sharedDataPath = (uuid) => `/Laboratory/customer-submissions/${encodeURIComponent(uuid)}/shared-data`;
export function loadLaboratorySharedData(uuid) {
  return request(sharedDataPath(uuid));
}
export function loadLaboratorySharedFiles(uuid, sample = null, offset = 0) {
  const params = new URLSearchParams({ offset: String(offset), ...(sample === null ? {} : { sample: String(sample) }) });
  return request(`${sharedDataPath(uuid)}/files?${params}`);
}
export function matchLaboratorySharedFiles(uuid, names) {
  return request(`${sharedDataPath(uuid)}/match`, { method: 'POST', body: JSON.stringify({ names }) });
}
export function startLaboratoryShare(uuid, note) {
  return request(sharedDataPath(uuid), { method: 'POST', body: JSON.stringify({ note }) });
}
export function tellLaboratoryShare(uuid, batch) {
  return request(`${sharedDataPath(uuid)}/${encodeURIComponent(batch)}/tell`, { method: 'POST', body: '{}' });
}
export function removeLaboratorySharedFile(file) {
  return request(`/Laboratory/customer-shared-files/${encodeURIComponent(file)}`, { method: 'DELETE' });
}
export async function openLaboratorySharedFile(file) {
  const { url } = await request(`/Laboratory/customer-shared-files/${encodeURIComponent(file)}`);
  window.location.assign(url);
}

// How many parts are signed in one go. A link lasts an hour, so they are asked for as they are needed.
const SHARED_PARTS_AT_ONCE = 16;

// One file of a share, straight to object storage in parts, as the Uploads page sends a file: a dropped
// connection costs a part, and the parts are put together and proved whole at the end. `sample` is the
// sample's place in the project, null for the whole project. `onProgress` gets the share done, 0 to 1.
export async function sendLaboratorySharedFile(uuid, batch, file, sample, onProgress) {
  const made = await request(`${sharedDataPath(uuid)}/${encodeURIComponent(batch)}/files`, {
    method: 'POST', body: JSON.stringify({ name: file.name, bytes: file.size, sample }),
  });
  const filePath = `/Laboratory/customer-shared-files/${encodeURIComponent(made.uuid)}`;
  const done = [];
  const loaded = new Map();
  const report = () => {
    if (!onProgress || file.size === 0) return;
    onProgress([...loaded.values()].reduce((sum, bytes) => sum + bytes, 0) / file.size);
  };
  for (let first = 1; first <= made.partCount; first += SHARED_PARTS_AT_ONCE) {
    const numbers = Array.from({ length: Math.min(SHARED_PARTS_AT_ONCE, made.partCount - first + 1) }, (_unused, index) => first + index);
    const { parts } = await request(`${filePath}/parts`, { method: 'POST', body: JSON.stringify({ numbers }) });
    const queue = [...parts];
    let failure = null;
    const worker = async () => {
      for (;;) {
        const part = queue.shift();
        if (!part || failure) return;
        const blob = file.slice(part.offset, part.offset + part.bytes);
        for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt += 1) {
          try {
            const etag = await putPart(part.url, blob, (bytes) => { loaded.set(part.number, bytes); report(); });
            loaded.set(part.number, part.bytes);
            report();
            done.push({ number: part.number, etag });
            break;
          } catch (error) {
            loaded.set(part.number, 0);
            report();
            if (attempt === PART_ATTEMPTS) { failure = error; return; }
            await new Promise((resolve) => { setTimeout(resolve, 500 * attempt); });
          }
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, parts.length) }, worker));
    if (failure) throw failure;
  }
  await request(`${filePath}/complete`, { method: 'POST', body: JSON.stringify({ parts: done }) });
  return made.uuid;
}

// A filled receiving form read back (nothing recorded): each tube's QC measurements, for the batch page.
export function readLaboratoryQcForm(file) {
  return request('/Laboratory/qc-forms/read', { method: 'POST', body: JSON.stringify({ file }) });
}

function laboratoryQuery(options) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(options)) {
    if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
  }
  return params.toString();
}

// ----------------------------------------------------------- organizations

export async function loadOrganizations({ query = '', active = 'all' } = {}) {
  const body = await request(`/Organizations?${laboratoryQuery({ query, active })}`);
  return { organizations: listField(body, 'organizations'), mayManage: field(body, 'mayManage') };
}

export async function loadOrganizationTypes() {
  return listField(await request('/Organizations/types'), 'types');
}

export async function loadOrganization(id) {
  const body = await request(`/Organizations/${encodeURIComponent(id)}`);
  return { organization: field(body, 'organization'), subprojects: listField(body, 'subprojects') };
}

export async function saveOrganization(id, fields) {
  const body = await request(id === null ? '/Organizations' : `/Organizations/${encodeURIComponent(id)}`, {
    method: id === null ? 'POST' : 'PATCH', body: JSON.stringify(fields),
  });
  return field(body, 'organization');
}

export function setOrganizationSampleCode(organizationId, sampleCode, code) {
  return request(`/Organizations/${encodeURIComponent(organizationId)}/sample-codes`, {
    method: 'POST', body: JSON.stringify({ sampleCode, code }),
  });
}

// ------------------------------------------------- customers (organizations)

// Every customer with what is open (accounts, submissions, those waiting for samples, invoices due), by id.
export async function loadCustomers() {
  return listField(await request('/Customers'), 'customers');
}

// What the customer forms choose from: workflows, currencies and our staff.
export async function loadCustomerOptions() {
  const body = await request('/Customers/options');
  return { workflows: listField(body, 'workflows'), currencies: listField(body, 'currencies'), staff: listField(body, 'staff'),
    laboratories: listField(body, 'laboratories') };
}

export async function loadCustomer(id) {
  return request(`/Customers/${encodeURIComponent(id)}`);
}

export async function saveCustomerDetails(id, fields) {
  return field(await request(`/Customers/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) }), 'customer');
}

// A new project (one-time or continuous: what we ask for, at its price), and who of theirs was emailed about it.
export async function createCustomerSubmission(id, submission) {
  const body = await request(`/Customers/${encodeURIComponent(id)}/submissions`, { method: 'POST', body: JSON.stringify(submission) });
  field(body, 'submission');
  return body;
}

// A pro forma invoice attached to a submission, and who of theirs was emailed about it.
export async function createCustomerInvoice(submissionUuid, invoice) {
  const body = await request(`/Customers/submissions/${encodeURIComponent(submissionUuid)}/invoices`, { method: 'POST', body: JSON.stringify(invoice) });
  field(body, 'invoice');
  return body;
}

export async function markCustomerInvoicePaid(uuid, reference) {
  return field(await request(`/Customers/invoices/${encodeURIComponent(uuid)}/paid`, {
    method: 'POST', body: JSON.stringify({ reference }),
  }), 'invoice');
}

// An invitation to the customer's portal: its address comes back once, with whether it was emailed.
export async function sendCustomerLink(id, link) {
  return request(`/Customers/${encodeURIComponent(id)}/links`, { method: 'POST', body: JSON.stringify(link) });
}

// Our billing profiles: the companies our invoices are issued by, each with a bank account for each
// currency, and the currencies an account can be in.
export async function loadBillingProfiles() {
  const body = await request('/Customers/billing-profiles');
  return { profiles: listField(body, 'profiles'), currencies: listField(body, 'currencies') };
}

// A billing profile saved: a new one (no id), or one changed; its company, bank accounts and whether it is in use.
export async function saveBillingProfile(id, details) {
  const body = await request(id === null ? '/Customers/billing-profiles' : `/Customers/billing-profiles/${encodeURIComponent(id)}`,
    { method: id === null ? 'POST' : 'PATCH', body: JSON.stringify(details) });
  return field(body, 'profile');
}

// Every currency we have with its European Central Bank rate against the euro, when there is one.
export async function loadExchangeRates() {
  const body = await request('/ExchangeRates');
  return { base: body?.base ?? 'EUR', rateDate: body?.rateDate ?? null, currencies: listField(body, 'currencies') };
}

// A project taken back before any samples are sent for it.
export async function withdrawCustomerSubmission(uuid) {
  return field(await request(`/Customers/submissions/${encodeURIComponent(uuid)}/withdraw`, { method: 'POST', body: '{}' }), 'submission');
}

// A continuous project closed to new submissions.
export async function closeCustomerProject(uuid) {
  return field(await request(`/Customers/submissions/${encodeURIComponent(uuid)}/close`, { method: 'POST', body: '{}' }), 'submission');
}

export async function loadCustomerSubmission(uuid) {
  return request(`/Customers/submissions/${encodeURIComponent(uuid)}`);
}

// A file of a customer's submission (theirs or our laboratory's), in a new tab.
export function openCustomerFile(uuid) {
  return openLink(() => request(`/Customers/files/${encodeURIComponent(uuid)}`));
}

// A submission's quote (by the submission) or a pro forma invoice, as a page to print. Fetched with
// the session, then shown in the tab opened by the click (opened first, so the browser does not block it).
export async function openCustomerDocument(kind, uuid) {
  const opened = window.open('', '_blank');
  try {
    const response = await rawRequest(`/Customers/documents/${encodeURIComponent(kind)}/${encodeURIComponent(uuid)}`);
    const url = URL.createObjectURL(new Blob([await response.text()], { type: 'text/html' }));
    if (opened) opened.location.href = url;
    else window.location.assign(url);
  } catch (error) {
    if (opened) opened.close();
    throw error;
  }
}

// --------------------------------------------------------------- companies

export async function loadCompanyCatalogue() {
  const body = await request('/Companies');
  return {
    companies: listField(body, 'companies'),
    reportTypes: listField(body, 'reportTypes'),
    languages: listField(body, 'languages'),
  };
}

export async function loadCompanyDetail(companyId) {
  const body = await request(`/Companies/${encodeURIComponent(companyId)}`);
  return field(body, 'company');
}

export function prepareCompanyLogoUpload(declaration, companyId = null) {
  const route = companyId === null
    ? '/Companies/logo-upload-url'
    : `/Companies/${encodeURIComponent(companyId)}/logo-upload-url`;
  return request(route, { method: 'POST', body: JSON.stringify(declaration) });
}

export async function putCompanyLogo(putUrl, file) {
  const response = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/svg+xml' },
    body: file,
  });
  if (!response.ok) {
    throw new XomicsApiError('The company logo could not be uploaded', {
      status: response.status,
      code: 'COMPANY_LOGO_UPLOAD_FAILED',
    });
  }
}

export async function createCompany(details) {
  const body = await request('/Companies', {
    method: 'POST', body: JSON.stringify(details),
  });
  return field(body, 'company');
}

export async function updateCompany(companyId, details) {
  const body = await request(`/Companies/${encodeURIComponent(companyId)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
  return field(body, 'company');
}

// ---------------------------------------------------------------- messaging

export async function loadMessageDeliveries({
  status = '', channel = '', limit = 100, offset = 0,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('status', status);
  if (channel) params.set('channel', channel);
  const body = await request(`/Messaging/deliveries?${params.toString()}`);
  return {
    deliveries: listField(body, 'deliveries'),
    summary: listField(body, 'summary'),
    total: numberField(body, 'total'),
    limit: numberField(body, 'limit'),
    offset: numberField(body, 'offset'),
  };
}

// Orders and samples share one carrier contract. Only the subject-specific
// context differs: an order already owns its destination, while a sample names
// the selected transit step and destination location.
// Step one of shipping an order: the box, the return labels and which carriers
// can take it with them. No carrier is asked for a price.
export async function loadCarrierOptions({ subjectType, subjectUuid, draft, boxCode, returnLabels }) {
  const body = await request('/Carrier/options', {
    method: 'POST',
    body: JSON.stringify({ subjectType, subjectUuid, draft, boxCode, returnLabels }),
  });
  field(body, 'parcel');
  listField(body, 'boxes');
  listField(body, 'carriers');
  return body;
}

export async function loadCarrierQuotes({
  subjectType, subjectUuid, draft, stageCode = null, destinationLocationId = null,
  weightGrams, returnLabels, boxCode,
}) {
  const body = await request('/Carrier/quotes', {
    method: 'POST',
    body: JSON.stringify({ subjectType, subjectUuid, draft, stageCode, destinationLocationId, weightGrams, returnLabels, boxCode }),
  });
  field(body, 'parcel');
  listField(body, 'providers');
  listField(body, 'quotes');
  return body;
}

export function purchaseCarrierLabel({
  subjectType, subjectUuid, draft, quoteId, stageCode = null,
  destinationLocationId = null, note = null,
  weightGrams, returnLabels, boxCode,
}) {
  return request('/Carrier/labels', {
    method: 'POST',
    body: JSON.stringify({
      subjectType, subjectUuid, draft, quoteId, stageCode, destinationLocationId, note, weightGrams, returnLabels, boxCode,
    }),
  });
}

// A freeform shipment's address form. A freeform shipment goes anywhere: the
// form offers every country, `officeCountry` being the office's own.
export async function loadShipmentCountries() {
  const body = await request('/Carrier/address/countries');
  return { countries: listField(body, 'countries'), officeCountry: body.officeCountry ?? null };
}

// The company's other offices and laboratories, to send a freeform shipment
// to: each with who it is, how to reach it, and its address.
export async function loadShipmentLocations() {
  const body = await request('/Carrier/address/locations');
  return listField(body, 'locations');
}

// What an address needs in a country: whether a postcode, and one for show.
export async function loadShipmentAddressForm(countryCode) {
  const body = await request(`/Carrier/address/form?${new URLSearchParams({ country: countryCode })}`);
  return field(body, 'form');
}

// The recipient address search: addresses and postcodes in the country chosen
// in the form, and in the rest of the world only when that country does not
// have what was typed (XOmics decides that). It looks around `near`, the point
// of a postcode searched by and picked, and a suggestion found without the
// flat typed in front of it says that flat (`flat`).
export async function searchShipmentAddresses(query, countryCode, near) {
  const body = await request('/Carrier/places/search', {
    method: 'POST',
    body: JSON.stringify({ query, form: { countryCode: countryCode || undefined, near: near || undefined } }),
  });
  return listField(body, 'suggestions');
}

// A chosen suggestion as the form's parts, whatever it has of them, its country too.
export async function loadShipmentPlaceParts(placeId) {
  const body = await request('/Carrier/places/parts', {
    method: 'POST',
    body: JSON.stringify({ placeId }),
  });
  return field(body, 'parts');
}

// The address as it stands in the form, chosen or typed, checked: it comes
// back with its region and its point on the map, or is refused with why.
export async function checkShipmentAddress(address) {
  const body = await request('/Carrier/address', {
    method: 'POST',
    body: JSON.stringify(address),
  });
  return field(body, 'location');
}

// Dispatching an order's shipment takes what it holds from the stock of the
// office that sends it; `stockTaken` in the answer says what.
export function dispatchCarrierShipment(shipmentUuid) {
  return request(`/Carrier/shipments/${encodeURIComponent(shipmentUuid)}/dispatch`, {
    method: 'POST', body: JSON.stringify({}),
  });
}

// What dispatching a shipment will take from its office's stock: a line per
// product, or per combination of one, with how many are sent (`quantity`) and
// how many the office has now (`stock`). Read before the dispatch is confirmed.
export async function loadShipmentStock(shipmentUuid) {
  const body = await request(`/Carrier/shipments/${encodeURIComponent(shipmentUuid)}/stock`);
  return listField(body, 'lines');
}

// What the account's own location has of a product: how many (`stock`), and
// for a product with choices how many of each combination (`combinations`,
// with the `choices` they are combinations of). A freeform shipment asks
// which ones it sends.
export function loadShipmentProductStock(productId) {
  return request(`/Carrier/products/${encodeURIComponent(productId)}/stock`);
}

export function createOrder(details) {
  return request('/Orders', { method: 'POST', body: JSON.stringify(details) });
}

export { ORDERS_PAGE_LIMIT };

// ---------------------------------------------------------------- products

// The catalogue, the same for every office. With an office, each product also
// carries what that office does with it: `prices` are the office's own, and
// `isSold` and `missingCurrencies` say whether it sells the product and which
// prices it still needs (the store per office, below).
export async function loadProducts(officeId = null) {
  const query = officeId === null ? '' : `?${new URLSearchParams({ office: String(officeId) })}`;
  const body = await request(`/Products${query}`);
  return listField(body, 'products');
}

export async function loadKitPipelines() {
  const body = await request('/Products/pipelines');
  return listField(body, 'pipelines');
}

export function createProduct(details) {
  return request('/Products', { method: 'POST', body: JSON.stringify(details) });
}

export function prepareProductImageUpload(productId, declaration) {
  return request(`/Products/${encodeURIComponent(productId)}/images/upload-url`, {
    method: 'POST', body: JSON.stringify(declaration),
  });
}

// This URL is scoped to one R2 object and expires quickly. It is intentionally
// not routed through request(): adding API cookies or an Authorization header to
// another origin would trigger the wrong CORS contract.
export async function putProductImage(putUrl, file) {
  const response = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) {
    throw new XomicsApiError('The image could not be uploaded', {
      status: response.status,
      code: 'PRODUCT_IMAGE_UPLOAD_FAILED',
    });
  }
}

export function completeProductImageUpload(productId, declaration) {
  return request(`/Products/${encodeURIComponent(productId)}/images/complete`, {
    method: 'POST', body: JSON.stringify(declaration),
  });
}

export function setProductMainImage(productId, imageId) {
  return request(`/Products/${encodeURIComponent(productId)}/images/${encodeURIComponent(imageId)}/main`, {
    method: 'POST', body: JSON.stringify({}),
  });
}

// A photo's description and the option values it shows ([{ option, value }] codes).
export function updateProductImage(productId, imageId, changes) {
  return request(`/Products/${encodeURIComponent(productId)}/images/${encodeURIComponent(imageId)}`, {
    method: 'PATCH', body: JSON.stringify(changes),
  });
}

export function removeProductImage(productId, imageId) {
  return request(`/Products/${encodeURIComponent(productId)}/images/${encodeURIComponent(imageId)}`, {
    method: 'DELETE',
  });
}

// Every photo of the product, once, in gallery order.
export function orderProductImages(productId, imageIds) {
  return request(`/Products/${encodeURIComponent(productId)}/images/order`, {
    method: 'POST', body: JSON.stringify({ imageIds }),
  });
}

// The places the storefront's product pages draw photos in: [{ code, name }].
export async function loadProductImagePlaces() {
  const body = await request('/Products/image-places');
  return listField(body, 'places');
}

export function updateProduct(id, changes) {
  return request(`/Products/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(changes),
  });
}

// The store per office. The catalogue is one; whether a product is sold, at
// what price, and which bundles are offered is each office's own. A visitor
// gets the store of the office responsible for their country (Locations).
//
// The offices whose store this account may open (its own, or every office with
// the right to manage them all), each with the countries it holds, the
// countries of the storefront it serves (`markets`) and the currencies it has
// to price in.
export async function loadStoreOffices() {
  const body = await request('/Store/offices');
  return {
    offices: listField(body, 'offices'),
    ownOfficeId: body.ownOfficeId ?? null,
    canChooseAny: body.canChooseAny === true,
  };
}

// Of every bundle whether the office offers it (and of every product what the
// office does with it, which the product list carries too).
export function loadOfficeStore(officeId) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}`);
}

// What an office does with a product, saved in one go: whether it sells it
// (`isSold`), how many it has (`stock`, or `variantStock` by SKU), and its
// prices (`prices`: an amount in minor units per currency, null to take one
// off). What is left out stays as it is; all of it is kept, or none.
export function saveOfficeProduct(officeId, productId, changes) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/products/${encodeURIComponent(productId)}`, {
    method: 'PATCH', body: JSON.stringify(changes),
  });
}

// An office's stock of one product: how many it has now (`stock`; of each
// combination in `combinations` where the product has choices, with the
// `choices` they are combinations of), the reasons a change can have
// (`reasons`, each adding or taking) and a page of what happened to it,
// newest first (`movements`, `hasMore`), with how many of each kind there are
// (`counts`). The history is narrowed by `kind` ('added', 'removed' or
// 'shipped'), by `search` (words of a note, of who made a change or of an
// order's reference) and to one combination (`sku`), and read on from
// `before` (the id of the last movement shown).
export function loadOfficeStock(officeId, productId, {
  kind = '', search = '', before = null, sku = null,
} = {}) {
  const query = new URLSearchParams();
  if (kind) query.set('kind', kind);
  if (search.trim() !== '') query.set('search', search.trim());
  if (sku !== null) query.set('sku', sku);
  if (before !== null) query.set('before', String(before));
  const asked = query.toString();
  return request(`/Store/offices/${encodeURIComponent(officeId)}/products/${encodeURIComponent(productId)}/stock${asked ? `?${asked}` : ''}`);
}

// Adds to that stock or takes from it: why (`reasonCode`, which says whether
// it adds or takes), a `note`, and how many (`quantity`), or for a product
// with choices how many of each combination that changed (`lines`,
// [{ sku, quantity }]), all at once. Answers with the stock as it is after.
export function changeOfficeStock(officeId, productId, change) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/products/${encodeURIComponent(productId)}/stock`, {
    method: 'POST', body: JSON.stringify(change),
  });
}

// A warehouse's or a retail shop's stock, kept as an office's is. The places
// this account keeps the stock of (its own, or every one for an account that
// may pick any office), and its own location's id.
export function loadStockLocations() {
  return request('/Stock/locations');
}

// One warehouse or shop and how many it has of every product
// ({ location, products: [{ productId, stock, stockPerCombination }] }).
export function loadLocationStock(locationId) {
  return request(`/Stock/locations/${encodeURIComponent(locationId)}`);
}

// Its stock of one product, as loadOfficeStock answers an office's.
export function loadLocationProductStock(locationId, productId, {
  kind = '', search = '', before = null, sku = null,
} = {}) {
  const query = new URLSearchParams();
  if (kind) query.set('kind', kind);
  if (search.trim() !== '') query.set('search', search.trim());
  if (sku !== null) query.set('sku', sku);
  if (before !== null) query.set('before', String(before));
  const asked = query.toString();
  return request(`/Stock/locations/${encodeURIComponent(locationId)}/products/${encodeURIComponent(productId)}${asked ? `?${asked}` : ''}`);
}

// Adds to it or takes from it, as changeOfficeStock does an office's.
export function changeLocationStock(locationId, productId, change) {
  return request(`/Stock/locations/${encodeURIComponent(locationId)}/products/${encodeURIComponent(productId)}`, {
    method: 'POST', body: JSON.stringify(change),
  });
}

// An office asking a warehouse for stock. The warehouses it can ask, nearest
// first, each with what it has of the products named.
export function loadRequestWarehouses(officeId, productIds) {
  const query = new URLSearchParams({ products: productIds.join(',') });
  return request(`/StockRequests/offices/${encodeURIComponent(officeId)}/warehouses?${query}`);
}

// An office's requests, newest first.
export function loadOfficeStockRequests(officeId) {
  return request(`/StockRequests/offices/${encodeURIComponent(officeId)}`);
}

// The requests a warehouse was sent, newest first.
export function loadWarehouseStockRequests(warehouseId) {
  return request(`/StockRequests/warehouses/${encodeURIComponent(warehouseId)}`);
}

// Asks a warehouse: `warehouseId`, `lines` [{ productId, sku?, quantity }], `note`.
export function createStockRequest(officeId, details) {
  return request(`/StockRequests/offices/${encodeURIComponent(officeId)}`, { method: 'POST', body: JSON.stringify(details) });
}

// One request with its lines, its label's downloads, and which side of it this account is on.
export function loadStockRequest(uuid) {
  return request(`/StockRequests/${encodeURIComponent(uuid)}`);
}

// Called off before a label was made, by either side.
export function cancelStockRequest(uuid, note = '') {
  return request(`/StockRequests/${encodeURIComponent(uuid)}/cancel`, {
    method: 'POST', body: JSON.stringify(note.trim() === '' ? {} : { note: note.trim() }),
  });
}

// The office takes in what arrived: its stock goes up by every line.
export function receiveStockRequest(uuid) {
  return request(`/StockRequests/${encodeURIComponent(uuid)}/receive`, { method: 'POST', body: '{}' });
}

// What an office asks for sending an order. `international`: its price in
// every currency (`amountMinor` null where it has none; `isNeeded` where its
// visitors pay in that currency), asked of every country that has no price of
// its own. `countries`: the countries it has given their own price.
// `countryCurrencies` and `fallbackCurrency` say which currency a country's
// price is in.
export function loadOfficeShipping(officeId) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/shipping`);
}

// Its shipping prices, saved in one go: `international` (currencies) and
// `countries`, each entry with an amount in minor units (0 is free) or null
// to take the price off. What is left out stays as it is.
export function saveOfficeShipping(officeId, { international, countries }) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/shipping`, {
    method: 'PATCH', body: JSON.stringify({ international, countries }),
  });
}

// The storefront's policies (privacy, terms, refunds, shipping) with one
// office's text of each.
export function loadOfficePolicies(officeId) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/policies`);
}

// One policy's text for one office, whole.
export function saveOfficePolicy(officeId, code, body) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/policies/${encodeURIComponent(code)}`, {
    method: 'PATCH', body: JSON.stringify({ body }),
  });
}

export function setBundleOffered(officeId, bundleId, isOffered) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/bundles/${encodeURIComponent(bundleId)}`, {
    method: 'PATCH', body: JSON.stringify({ isOffered }),
  });
}

// Starts an office that has no store from another office's, as it is now.
export function copyOfficeStore(officeId, fromOfficeId) {
  return request(`/Store/offices/${encodeURIComponent(officeId)}/copy`, {
    method: 'POST', body: JSON.stringify({ fromOfficeId }),
  });
}

// A product's options as one document (ProductOptions.js on the backend):
// saving sends the whole document, and whatever it leaves out is switched off.
// What a product's options and variants are is the catalogue's, the same for
// every office. How many there are of a variant is each office's own: with an
// office, the document carries (and saves) that office's stock of each.
const forOffice = (officeId) => (officeId === null ? '' : `?${new URLSearchParams({ office: String(officeId) })}`);

export function loadProductOptions(id, officeId = null) {
  return request(`/Products/${encodeURIComponent(id)}/options${forOffice(officeId)}`);
}

export function saveProductOptions(id, document, officeId = null) {
  return request(`/Products/${encodeURIComponent(id)}/options${forOffice(officeId)}`, {
    method: 'POST', body: JSON.stringify(document),
  });
}

// One variant's availability, by SKU, without touching the options.
export function updateProductVariant(id, sku, changes) {
  return request(`/Products/${encodeURIComponent(id)}/variants/${encodeURIComponent(sku)}`, {
    method: 'PATCH', body: JSON.stringify(changes),
  });
}

export function loadCommerceSettings() {
  return request('/CommerceSettings');
}

export function createProductCategory(details) {
  return request('/CommerceSettings/categories', {
    method: 'POST', body: JSON.stringify(details),
  });
}

export function updateProductCategory(id, details) {
  return request(`/CommerceSettings/categories/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// A category's photo goes up the way a product photo does: a PUT straight to
// storage (putProductImage), then XOmics checks it and makes its sizes.
export function prepareCategoryImageUpload(categoryId, declaration) {
  return request(`/CommerceSettings/categories/${encodeURIComponent(categoryId)}/image/upload-url`, {
    method: 'POST', body: JSON.stringify(declaration),
  });
}

export function completeCategoryImageUpload(categoryId, declaration) {
  return request(`/CommerceSettings/categories/${encodeURIComponent(categoryId)}/image/complete`, {
    method: 'POST', body: JSON.stringify(declaration),
  });
}

export function removeCategoryImage(categoryId) {
  return request(`/CommerceSettings/categories/${encodeURIComponent(categoryId)}/image`, {
    method: 'DELETE',
  });
}

export function createCurrency(details) {
  return request('/CommerceSettings/currencies', {
    method: 'POST', body: JSON.stringify(details),
  });
}

export function updateCurrency(code, details) {
  return request(`/CommerceSettings/currencies/${encodeURIComponent(code)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

export function updateStorefrontCountry(countryCode, details) {
  return request(`/CommerceSettings/countries/${encodeURIComponent(countryCode)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

export async function loadCoupons() {
  const body = await request('/Coupons');
  return listField(body, 'coupons');
}

export function createCoupon(details) {
  return request('/Coupons', { method: 'POST', body: JSON.stringify(details) });
}

export function updateCoupon(id, details) {
  return request(`/Coupons/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// Bundles: products that cost less together, or more of one product for
// less. The bag gives the saving by itself; no code is typed.
export async function loadBundles() {
  const body = await request('/Bundles');
  return listField(body, 'bundles');
}

export function createBundle(details) {
  return request('/Bundles', { method: 'POST', body: JSON.stringify(details) });
}

export function updateBundle(id, details) {
  return request(`/Bundles/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// Reviews of a product, as its page on the storefront shows them: every
// review, or one product's.
export async function loadProductReviews(productId = null) {
  const query = productId === null ? '' : `?${new URLSearchParams({ productId: String(productId) })}`;
  const body = await request(`/ProductReviews${query}`);
  return listField(body, 'reviews');
}

export function createProductReview(details) {
  return request('/ProductReviews', { method: 'POST', body: JSON.stringify(details) });
}

export function updateProductReview(id, details) {
  return request(`/ProductReviews/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// The menu, the Longevity Hub, the quiz and the help centre are written once
// and shared by the offices; what an office's store shows of them is its own.
// Their pages are drawn for one office at a time, the one named under their
// title (StoreOffice.js ForStoreOffice says which before a page is drawn), and
// every request of theirs says whose store it reads or changes.
let contentOfficeId = null;
export function setContentOffice(officeId) {
  contentOfficeId = officeId;
}
function ofOffice(path) {
  if (contentOfficeId === null) throw new Error('No office is chosen for this page');
  return `${path}${path.includes('?') ? '&' : '?'}${new URLSearchParams({ officeId: String(contentOfficeId) })}`;
}

// The storefront help centre: its topics, their guides, groups of questions
// and the first page's own list, each text with the row of its English and,
// for another language, that language's row beside it. A text is changed
// through the translation endpoints (saveTranslation, and the import ones for
// a language's missing words); these add a question, show or hide a question
// or a whole section for the office, and set the order of a section's questions.
export async function loadHelpCentre(language) {
  const body = await request(ofOffice(`/HelpCentre?${new URLSearchParams({ language })}`));
  return field(body, 'helpCentre');
}

export function createHelpQuestion(details) {
  return request(ofOffice('/HelpCentre/questions'), { method: 'POST', body: JSON.stringify(details) });
}

export function setHelpQuestionShown(id, isActive) {
  return request(ofOffice(`/HelpCentre/questions/${encodeURIComponent(id)}`), {
    method: 'PATCH', body: JSON.stringify({ isActive }),
  });
}

export function setHelpSectionShown(id, isActive) {
  return request(ofOffice(`/HelpCentre/sections/${encodeURIComponent(id)}`), {
    method: 'PATCH', body: JSON.stringify({ isActive }),
  });
}

export function setHelpQuestionOrder(sectionId, questionIds) {
  return request(ofOffice(`/HelpCentre/sections/${encodeURIComponent(sectionId)}/order`), {
    method: 'PATCH', body: JSON.stringify({ questionIds }),
  });
}

// The storefront's Longevity Hub: its first page and every guide and article,
// each made of sections that list entries. Texts come as the help centre's do
// (the English row and, for another language, that language's row beside it)
// and are changed through the translation endpoints; these add a page, a
// section or an entry, publish, show, hide or remove, set the order, and give
// a photo, a link, a look, a drawing or a category.
export async function loadHub(language) {
  const body = await request(ofOffice(`/Hub?${new URLSearchParams({ language })}`));
  return field(body, 'hub');
}

export async function loadHubPage(id, language) {
  const body = await request(ofOffice(`/Hub/pages/${encodeURIComponent(id)}?${new URLSearchParams({ language })}`));
  return { language: field(body, 'language'), page: field(body, 'page') };
}

const hubChange = (path, method, body) => request(ofOffice(`/Hub/${path}`), { method, body: JSON.stringify(body) });

export const createHubPage = (details) => hubChange('pages', 'POST', details);
export const updateHubPage = (id, changes) => hubChange(`pages/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setHubPageOrder = (pageIds) => hubChange('pages/order', 'PATCH', { pageIds });
export const createHubSection = (pageId, details) => hubChange(`pages/${encodeURIComponent(pageId)}/sections`, 'POST', details);
export const updateHubSection = (id, changes) => hubChange(`sections/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setHubSectionOrder = (pageId, sectionIds) => hubChange(`pages/${encodeURIComponent(pageId)}/order`, 'PATCH', { sectionIds });
export const createHubItem = (sectionId, kind) => hubChange(`sections/${encodeURIComponent(sectionId)}/items`, 'POST', { kind });
export const updateHubItem = (id, changes) => hubChange(`items/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setHubItemOrder = (sectionId, itemIds) => hubChange(`sections/${encodeURIComponent(sectionId)}/order`, 'PATCH', { itemIds });
export const createHubCategory = (details) => hubChange('categories', 'POST', details);
export const updateHubCategory = (id, changes) => hubChange(`categories/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setHubCategoryOrder = (categoryIds) => hubChange('categories/order', 'PATCH', { categoryIds });

// The storefront's quiz: its questions with their answers, the products it
// can recommend, what each answer recommends (`lifts`: the products it points
// to, each with a strength from 1 to 4), and what visitors answered. Texts
// come and are changed as the Hub's are.
export async function loadQuiz(language) {
  const body = await request(ofOffice(`/Quiz?${new URLSearchParams({ language })}`));
  return field(body, 'quiz');
}

const quizChange = (path, method, body) => request(ofOffice(`/Quiz/${path}`), { method, body: JSON.stringify(body ?? {}) });

export const createQuizQuestion = () => quizChange('questions', 'POST');
export const updateQuizQuestion = (id, changes) => quizChange(`questions/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setQuizQuestionOrder = (questionIds) => quizChange('questions/order', 'PATCH', { questionIds });
export const createQuizOption = (questionId) => quizChange(`questions/${encodeURIComponent(questionId)}/options`, 'POST');
export const updateQuizOption = (id, changes) => quizChange(`options/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setQuizOptionOrder = (questionId, optionIds) => quizChange(`questions/${encodeURIComponent(questionId)}/order`, 'PATCH', { optionIds });
export const setQuizLifts = (optionId, lifts) => quizChange(`options/${encodeURIComponent(optionId)}/lifts`, 'PATCH', { lifts });
export const createQuizProduct = (productId) => quizChange('products', 'POST', { productId });
export const updateQuizProduct = (id, changes) => quizChange(`products/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setQuizProductOrder = (productIds) => quizChange('products/order', 'PATCH', { productIds });

export async function loadQuizSummary(days) {
  const body = await request(`/Quiz/responses/summary?${new URLSearchParams({ days: String(days) })}`);
  return field(body, 'summary');
}

export async function loadQuizResponses({ limit = 50, beforeId = null } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (beforeId !== null) params.set('beforeId', String(beforeId));
  const body = await request(`/Quiz/responses?${params}`);
  return { responses: listField(body, 'responses'), nextBeforeId: body.nextBeforeId ?? null };
}

// The storefront's menu: the names of its top row, each with the columns,
// links and picture cards of its panel, every text with its rows. Its words
// are changed through the translation endpoints; these add a row, show, hide
// or remove it, set the order, and say where a row leads and what a card shows.
export async function loadMenu(language) {
  const body = await request(ofOffice(`/Menu?${new URLSearchParams({ language })}`));
  return field(body, 'menu');
}

const menuChange = (path, method, body) => request(ofOffice(`/Menu/${path}`), { method, body: JSON.stringify(body ?? {}) });

// A new name of the top row, made with the English name it starts with.
export const createMenuEntry = (name = '') => menuChange('entries', 'POST', name === '' ? undefined : { name });
export const updateMenuEntry = (id, changes) => menuChange(`entries/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setMenuEntryOrder = (entryIds) => menuChange('entries/order', 'PATCH', { entryIds });
export const createMenuColumn = (entryId) => menuChange(`entries/${encodeURIComponent(entryId)}/columns`, 'POST');
export const updateMenuColumn = (id, changes) => menuChange(`columns/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setMenuColumnOrder = (entryId, columnIds) => menuChange(`entries/${encodeURIComponent(entryId)}/columns/order`, 'PATCH', { columnIds });
export const createMenuLink = (columnId) => menuChange(`columns/${encodeURIComponent(columnId)}/links`, 'POST');
export const updateMenuLink = (id, changes) => menuChange(`links/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setMenuLinkOrder = (columnId, linkIds) => menuChange(`columns/${encodeURIComponent(columnId)}/links/order`, 'PATCH', { linkIds });
export const createMenuCard = (entryId) => menuChange(`entries/${encodeURIComponent(entryId)}/cards`, 'POST');
export const updateMenuCard = (id, changes) => menuChange(`cards/${encodeURIComponent(id)}`, 'PATCH', changes);
export const setMenuCardOrder = (entryId, cardIds) => menuChange(`entries/${encodeURIComponent(entryId)}/cards/order`, 'PATCH', { cardIds });

// A Hub photo: the browser puts the file straight into storage at the address
// XOmics prepares (putProductImage), then XOmics checks it and makes its sizes.
export const prepareHubImageUpload = (declaration) => hubChange('images/upload-url', 'POST', declaration);
export async function completeHubImageUpload(details) {
  return field(await hubChange('images/complete', 'POST', details), 'image');
}

// ------------------------------------------------------- commerce finance

function commerceFinanceQuery({
  from, to, status = '', currency = '', q = '', limit, beforeId = null,
}) {
  const query = new URLSearchParams({ from: String(from), to: String(to) });
  if (status) query.set('status', status);
  if (currency) query.set('currency', currency);
  if (q) query.set('q', q);
  if (limit !== undefined) query.set('limit', String(limit));
  if (beforeId !== null) query.set('before_id', String(beforeId));
  return query.toString();
}

export function loadCommerceEconomics({ from, to }) {
  return request(`/Transactions/overview?${commerceFinanceQuery({ from, to })}`);
}

export function loadCommerceAnalytics({ from, to, currency }) {
  return request(`/Transactions/analytics?${commerceFinanceQuery({ from, to, currency })}`);
}

export function loadTransactions(filters) {
  return request(`/Transactions?${commerceFinanceQuery(filters)}`);
}

// Finance: every movement of money (store payments and refunds, customer invoices, label costs),
// the facts of a window, and what the filters offer.
function financeQuery(filters) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return query.toString();
}

export function loadFinanceOptions() {
  return request('/Finance/options');
}

export function loadFinanceSummary(filters) {
  return request(`/Finance/summary?${financeQuery(filters)}`);
}

export function loadFinanceLedger(filters) {
  return request(`/Finance/ledger?${financeQuery(filters)}`);
}

// The ledger as a CSV file, saved where the browser saves downloads.
export async function downloadFinanceLedger(filters) {
  const response = await rawRequest(`/Finance/ledger/export?${financeQuery(filters)}`);
  const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '')?.[1] || 'finance.csv';
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url; link.download = name;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ------------------------------------------------------------------ audit

// Records that this account opened a screen. Fire and forget: it is a record,
// and a page must never fail to render because writing one did.
export function recordPageView(path) {
  return request('/Audit/view', { method: 'POST', body: JSON.stringify({ path }) });
}

// Where you have been. Read back out of the audit log, so it cannot disagree
// with what was actually opened.
export async function loadRecents(limit = 5) {
  const body = await request(`/Audit/recents?limit=${limit}`);
  return listField(body, 'recents');
}


// Keyset paging on the id, not an offset: the log is append-only and busy, so
// an offset shifts under you between pages and silently repeats or skips rows.
// The audit log's narrow filters, as its list and its summary both take them.
function auditQuery({ actorType = '', action = '', outcome = '', accountId = null, since = null }) {
  const params = new URLSearchParams();
  if (actorType) params.set('actor_type', actorType);
  if (action) params.set('action', action);
  if (outcome) params.set('outcome', outcome);
  if (accountId !== null) params.set('account_id', String(accountId));
  if (since !== null) params.set('since', String(since));
  return params;
}

export async function loadAudit({ limit = 50, beforeId = null, ...filters } = {}) {
  const params = auditQuery(filters);
  params.set('limit', String(limit));
  if (beforeId !== null) params.set('before_id', String(beforeId));
  const body = await request(`/Audit?${params}`);
  return {
    events: listField(body, 'events'),
    nextBeforeId: field(body, 'next_before_id'),
  };
}

// What a range of the log adds up to, counted by the server over every row.
export async function loadAuditSummary(filters = {}) {
  return request(`/Audit/summary?${auditQuery(filters)}`);
}

// The log's vocabulary: every action's label, and which filters exist.
export async function loadAuditActions() {
  return request('/Audit/actions');
}

// ------------------------------------------------------- role permissions

export async function loadRolePermissions() {
  const body = await request('/RolePermissions');
  return {
    roles: listField(body, 'roles'),
    permissions: listField(body, 'permissions'),
  };
}

export function updateRolePermissions(roleCode, permissions) {
  return request(`/RolePermissions/${encodeURIComponent(roleCode)}`, {
    method: 'PATCH',
    body: JSON.stringify({ permissions }),
  });
}

// ------------------------------------------------------------- activation
//
// Deliberately not routed through request(): the person following an invitation
// link has no session, and request() would attach a stale token and try to
// refresh on the 401 that follows, logging out whoever else is using the tab.

async function publicRequest(path, options = {}) {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...options,
    headers: { Accept: 'application/json', ...(options.headers || {}) },
  });
  const body = await responseBody(response);
  if (!response.ok) throw errorFrom(response, body);
  return body;
}

export function loadInvitation(token) {
  return publicRequest(`/Activate/${encodeURIComponent(token)}`);
}

export function activateAccount(token, password) {
  return publicRequest(`/Activate/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
}

// -------------------------------------------------------------- overview

export function loadOverview() {
  // Sections the account is not permitted to see are absent, not zeroed, so
  // the screen can tell "not allowed" from "nothing happening".
  return request('/Overview');
}

export function loadAnalytics(days = 30) {
  return request(`/Analytics?days=${encodeURIComponent(days)}`);
}

export function loadWebserverOverview(days = 30) {
  return request(`/Platform/Webservers/overview?days=${encodeURIComponent(days)}`);
}

export function loadWebserverAnalytics({ days = 30, service = '' } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (service) params.set('service', service);
  return request(`/Platform/Webservers/analytics?${params}`);
}

export function loadWebserverRequests({ service = '', limit = 50, beforeId = null } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (service) params.set('service', service);
  if (beforeId !== null) params.set('beforeId', String(beforeId));
  return request(`/Platform/Webservers/requests?${params}`);
}

// The Mobile app section. Accounts, usage, wearables and notifications come from
// the mobile backend through XOmics; the patient behind an account from XOmics.
export function loadMobileView(view, days = 30) {
  return request(`/Platform/Mobile/${view}?days=${encodeURIComponent(days)}`);
}

export function loadMobileAccounts(filters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return request(`/Platform/Mobile/accounts${query ? `?${query}` : ''}`);
}

// One place's use of the app over a range: a country code and, if given, a
// city in it.
export function loadMobilePlace({ days, country, city = null }) {
  const params = new URLSearchParams({ days: String(days), country });
  if (city) params.set('city', city);
  return request(`/Platform/Mobile/places?${params}`);
}

// Every account matching the filters, read page by page (the list serves at
// most 100 at a time).
export async function loadAllMobileAccounts(filters = {}) {
  const accounts = [];
  for (let offset = 0; ; offset += 100) {
    const page = await loadMobileAccounts({ ...filters, limit: 100, offset });
    accounts.push(...page.accounts);
    if (page.accounts.length === 0 || offset + 100 >= page.total) return accounts;
  }
}

export function loadMobileAccount(uuid) {
  return request(`/Platform/Mobile/accounts/${encodeURIComponent(uuid)}`);
}

// A ring's or band's readings, for staff allowed to see wearable health data.
export function loadMobileDeviceHealth(deviceId, days = 30) {
  return request(`/Platform/Mobile/devices/${encodeURIComponent(deviceId)}/health?days=${encodeURIComponent(days)}`);
}

// The app's own labels, so the phone drawn in the portal says what the app says.
export function loadMobileAppStrings(language) {
  return request(`/Platform/Mobile/app-strings?language=${encodeURIComponent(language)}`);
}

export function linkMobileAccountPatient(uuid, patientId) {
  return request(`/Platform/Mobile/accounts/${encodeURIComponent(uuid)}/patient`, {
    method: 'PATCH',
    body: JSON.stringify({ patientId }),
  });
}

// From a patient's record the invitation names that patient, and the backend
// links the new account to them before the code is emailed. Left out otherwise.
export function inviteMobileUser({ email, firstName, lastName, patientId }) {
  return request('/MobileInvitations/invite', {
    method: 'POST',
    body: JSON.stringify(patientId === undefined ? { email, firstName, lastName } : { email, firstName, lastName, patientId }),
  });
}

export function resendMobileInvitation(email) {
  return request('/MobileInvitations/resend', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// ------------------------------------------------------- storefront analytics

// The E-commerce section's storefront analytics. Every read names its site; the
// server counts everything, the pages only draw it. Empty filters are left out.
function storefrontQuery(filters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function loadStorefrontSites() {
  const body = await request('/Platform/Storefront/sites');
  return listField(body, 'sites');
}

export function loadStorefrontLive(site) {
  return request(`/Platform/Storefront/live${storefrontQuery({ site })}`);
}

// One of the range reads: overview, acquisition, pages, products, checkout, audience or performance.
export function loadStorefrontView(view, { site, days }) {
  return request(`/Platform/Storefront/${view}${storefrontQuery({ site, days })}`);
}

export function loadStorefrontClicks({ site, days, path, device = '' }) {
  return request(`/Platform/Storefront/pages/clicks${storefrontQuery({ site, days, path, device })}`);
}

// The latest recorded view of a page on one kind of device: the page drawn behind its click map.
export function loadStorefrontPageSnapshot({ site, days, path, device }) {
  return request(`/Platform/Storefront/pages/snapshot${storefrontQuery({ site, days, path, device })}`);
}

export function loadStorefrontFilters({ site, days }) {
  return request(`/Platform/Storefront/filters${storefrontQuery({ site, days })}`);
}

export function loadStorefrontCampaigns({ site, days, status = '' }) {
  return request(`/Platform/Storefront/campaigns${storefrontQuery({ site, days, status })}`);
}

export function loadStorefrontCampaign(id, days) {
  return request(`/Platform/Storefront/campaigns/${encodeURIComponent(id)}${storefrontQuery({ days })}`);
}

export function createStorefrontCampaign(details) {
  return request('/Platform/Storefront/campaigns', { method: 'POST', body: JSON.stringify(details) });
}

export function updateStorefrontCampaign(id, details) {
  return request(`/Platform/Storefront/campaigns/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

export function createStorefrontLink(campaignId, details) {
  return request(`/Platform/Storefront/campaigns/${encodeURIComponent(campaignId)}/links`, {
    method: 'POST', body: JSON.stringify(details),
  });
}

export function updateStorefrontLink(id, details) {
  return request(`/Platform/Storefront/links/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

export function loadStorefrontSessions(filters) {
  return request(`/Platform/Storefront/sessions${storefrontQuery(filters)}`);
}

export function loadStorefrontSession(id) {
  return request(`/Platform/Storefront/sessions/${encodeURIComponent(id)}`);
}

// A page of a session's recording; `after` is the last chunk's seq, from `next`.
export function loadStorefrontReplay(id, { page = '', after = null } = {}) {
  return request(`/Platform/Storefront/sessions/${encodeURIComponent(id)}/replay${storefrontQuery({ page, after })}`);
}

// `props` filters events by what they carried: { 'element.track': 'hero-cta' }.
function eventQuery({ props = {}, ...filters }) {
  const query = storefrontQuery(filters);
  const params = new URLSearchParams(query.slice(1));
  for (const [path, value] of Object.entries(props)) {
    if (path && value !== '' && value !== null && value !== undefined) params.set(`prop.${path}`, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function loadStorefrontEvents(filters) {
  return request(`/Platform/Storefront/events${eventQuery(filters)}`);
}

export function loadStorefrontEventBreakdown(filters) {
  return request(`/Platform/Storefront/events/breakdown${eventQuery(filters)}`);
}

export function loadStorefrontSettings(site) {
  return request(`/Platform/Storefront/settings${storefrontQuery({ site })}`);
}

export function updateStorefrontSettings(site, details) {
  return request(`/Platform/Storefront/settings${storefrontQuery({ site })}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// Origins, channel rules and bot patterns are added and switched off, never deleted.
const STOREFRONT_SETTING_LISTS = { origins: 'origins', rules: 'channel-rules', bots: 'bot-patterns' };

export function addStorefrontSetting(site, list, details) {
  return request(`/Platform/Storefront/settings/${STOREFRONT_SETTING_LISTS[list]}${storefrontQuery({ site })}`, {
    method: 'POST', body: JSON.stringify(details),
  });
}

export function updateStorefrontSetting(site, list, id, details) {
  return request(`/Platform/Storefront/settings/${STOREFRONT_SETTING_LISTS[list]}/${encodeURIComponent(id)}${storefrontQuery({ site })}`, {
    method: 'PATCH', body: JSON.stringify(details),
  });
}

// ------------------------------------------------------------ dashboard layout

// The signed-in person's dashboard: their arrangement (or their role's default),
// the role default, and the widgets their permissions allow. Each widget has a
// place and a size on a grid `columns` wide. Saving replaces the whole
// arrangement; resetting removes it so the role default shows again.
function dashboardLayout(body) {
  return {
    role: field(body, 'role'),
    previewing: boolField(body, 'previewing'),
    source: field(body, 'source'),
    updatedAt: field(body, 'updatedAt'),
    columns: field(body, 'columns'),
    widgets: listField(body, 'widgets'),
    dropped: listField(body, 'dropped'),
    defaults: listField(body, 'defaults'),
    library: listField(body, 'library'),
  };
}

export async function loadDashboardLayout() {
  return dashboardLayout(await request('/Dashboard/layout'));
}

export async function saveDashboardLayout(widgets) {
  return dashboardLayout(await request('/Dashboard/layout', {
    method: 'PATCH', body: JSON.stringify({ widgets: widgets.map(({ code, x, y, w, h }) => ({ code, x, y, w, h })) }),
  }));
}

export async function resetDashboardLayout() {
  return dashboardLayout(await request('/Dashboard/layout', { method: 'DELETE' }));
}

// Feedback about any part of the platform. The attachment goes to storage the
// same way a product photo does: XOmics signs a URL, the browser puts the file
// there itself, and the report names the object once it is up.
export function prepareFeedbackMedia(declaration) {
  return request('/Feedback/media', { method: 'POST', body: JSON.stringify(declaration) });
}

export async function putFeedbackMedia(putUrl, file, contentType) {
  const response = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) {
    throw new XomicsApiError('The file could not be uploaded', {
      status: response.status,
      code: 'FEEDBACK_UPLOAD_FAILED',
    });
  }
}

export function sendFeedback(report) {
  return request('/Feedback', { method: 'POST', body: JSON.stringify(report) });
}

// The inbox, for staff holding FEEDBACK_READ. Paged by the marker the previous
// answer gave back rather than an offset, so the cost of a page does not grow
// with the table.
export function loadFeedbackReports(filters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '' && value !== null && value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return request(`/Feedback/reports${query ? `?${query}` : ''}`);
}

export function loadFeedbackReport(uuid) {
  return request(`/Feedback/reports/${encodeURIComponent(uuid)}`);
}

// A link that expires in five minutes; ask for it when the report is opened.
export function loadFeedbackMedia(uuid) {
  return request(`/Feedback/reports/${encodeURIComponent(uuid)}/media`);
}

export function setFeedbackStatus(uuid, status) {
  return request(`/Feedback/reports/${encodeURIComponent(uuid)}/status`, {
    method: 'PATCH', body: JSON.stringify({ status }),
  });
}
